import {
  DashboardState,
  EDatasourceType,
  ESdChangeReason,
  EUserAccessPart,
  IActionDefinitionDto,
  IActionFolder,
  IDataDiagramSchemeDto,
  IDatabaseTableModel,
  IDatasourceDto,
  ISessionVariable,
  isInlineCodeDatasourceType,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import { IRestApiEndpoint } from "@kuika/kuika-cl-model/src/dtos/datasource.dto";
import { ITableFolder } from "@kuika/kuika-cl-model/src/models/table-folder";
import { AxiosResponse } from "axios";
import _ from "lodash";
import * as React from "react";
import {
  Draggable,
  type DraggableProvided,
  DropResult,
  type DroppableProvided,
  type DroppableStateSnapshot
} from "react-beautiful-dnd";
import { connect } from "react-redux";
import { DataDiagramSchemeService } from "../../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../../services/datasource-service";
import { SESSION_VARIABLES_CHANGED_EVENT } from "../../../../services/designer-live/designer-change-routing";
import { OfflineTablesService } from "../../../../services/offline-tables-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  type LoadingPresentationBase,
  resolveLoadingPresentation
} from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { setNextCallbackFunction } from "../../../../store/unsaved-changes-modal/actions";
import { DeleteModal } from "../../../common/delete-modal";
import FolderModal, { type IFolderFormValues } from "../../../common/folder-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import "./data-diagram-scheme-window.scss";
import KuikaDropdownMenu, {
  type KuikaDropdownMenuItemType
} from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import {
  PointerAimedDragDropContext,
  PointerAimedDroppable
} from "../../../ui-kit/kuika-pointer-dnd/pointer-aimed-dnd";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg, { type KuikaSvgProps } from "../../../ui-kit/kuika-svg/kuika-svg";
import { ConnectorPickerPopover } from "../../data-diagram/connector-picker-popover";
import {
  canEditAction,
  findAgentUsagesOfAction,
  findRestEndpointIndex,
  getNewActionKindLabel,
  isTableEditable
} from "../../data-diagram/datasource-row-actions";
import {
  ActionFolderMenuItem,
  ActionMenuItem,
  ActionsGroupMenuItem,
  DatasourceMenuItem,
  getActionFolderMenuItems,
  getActionMenuItems,
  getActionsGroupMenuItems,
  getDatasourceMenuItems,
  getDatasourceRowAccess,
  getTableFolderMenuItems,
  getTableMenuItems,
  getTablesGroupMenuItems,
  IDatasourceRowAccess,
  TableFolderMenuItem,
  TableMenuItem,
  TablesGroupMenuItem
} from "../../data-diagram/datasource-row-menus";
import { getTableFolderMemberId, groupTablesBySchema } from "../../data-diagram/datasource-tree";
import {
  applyDatasourceRowMove,
  DatasourceFolderDialog,
  DatasourceFolderRef,
  datasourceFolderNameExists,
  deleteDatasourceFolder,
  getUnfiledTablesDroppableId,
  IDatasourceTreeActionDeps,
  resolveSourcesTreeTableDrop,
  startNewTable,
  submitDatasourceFolderDialog
} from "../../data-diagram/datasource-tree-actions";
import { DatasourceRowMove, findConnector, getDatasourceIconName } from "../../data-diagram/datasources-landing-model";
import { isSameTable } from "../../data-diagram/table-editor-view";
import { ActionUsagesModal } from "../datasource-panel/action-usages-modal";
import { DatasourceProviderBadge, providerBadgeMap } from "../datasource-panel/datasources";
import { SchemaNode } from "../datasource-panel/datasources";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import { FolderCreateComponent } from "./data-diagram-folder-add";

/** One entry of the kit collapse's `items`, derived so the tree need not import rc-collapse. */
type KuikaCollapseItem = NonNullable<React.ComponentProps<typeof KuikaCollapsePanel>["items"]>[number];

/**
 * The tree assembles its rows conditionally — a datasource without tables contributes no Tables
 * node. The kit drops falsy entries at render; this keeps the list honestly typed on the way in.
 */
const collapseItems = (entries: (KuikaCollapseItem | false | undefined | null)[]): KuikaCollapseItem[] =>
  entries.filter((entry): entry is KuikaCollapseItem => Boolean(entry));

/** What the kit collapse hands its `expandIcon`. */
interface ITreeChevronProps {
  isActive?: boolean;
}

/**
 * Every row's chevron — the Screens and Custom Actions trees' own: `k-down` / `k-right` at 16px, in
 * the glyph's resting `--dark-7`. It replaced nine hand-rolled ones: 8 and 10px arrows with a fill
 * baked into the file, and a sub-folder's 24px stroke drawn in black, which vanished on the dark
 * theme.
 */
const renderTreeChevron = ({ isActive }: ITreeChevronProps): React.ReactNode => (
  <KuikaSvg name={isActive ? "k-down" : "k-right"} type="arrows" width={16} height={16} />
);

/**
 * A folder with a row dragged over it: its own header takes the drop highlight the Screens and
 * Custom Actions folders show (`design-left-panel.scss`). Only the modifier — the base
 * `kuika-left-panel__folder-collapse` also hides the row's "…" with `visibility`, which would fight
 * this tree's own opacity reveal (form.scss).
 */
const getFolderDropClassName = (snapshot: DroppableStateSnapshot): string | undefined =>
  snapshot.isDraggingOver ? "kuika-left-panel__folder-collapse--drag-over" : undefined;

/** The type a column or a parameter is shown with — what `getTypeIcon` and its siblings map a type name to. */
type TypeGlyph =
  | "string"
  | "boolean"
  | "double"
  | "decimal"
  | "int16"
  | "int32"
  | "int64"
  | "guid"
  | "date"
  | "date-time"
  | "date-time-offset"
  | "location"
  | "file-content"
  | "image";

interface ITypeGlyphAsset {
  type: NonNullable<KuikaSvgProps["type"]>;
  name: string;
}

/**
 * Each type's glyph: the `action-icons` set the SQL action editor draws the same types with
 * (`shared/utility/data-type-icon.ts`), masked so it takes the row's colour — the hex-filled
 * components these rows used to draw stayed one grey in both themes. That set has no image glyph,
 * so an image column takes the common one.
 */
const TYPE_GLYPH_ASSETS: Record<TypeGlyph, ITypeGlyphAsset> = {
  string: { type: "action-icons", name: "string" },
  boolean: { type: "action-icons", name: "boolean" },
  double: { type: "action-icons", name: "double" },
  decimal: { type: "action-icons", name: "decimal" },
  int16: { type: "action-icons", name: "int16" },
  int32: { type: "action-icons", name: "int32" },
  int64: { type: "action-icons", name: "int64" },
  guid: { type: "action-icons", name: "guid" },
  date: { type: "action-icons", name: "calendar-month" },
  "date-time": { type: "action-icons", name: "date&time" },
  "date-time-offset": { type: "action-icons", name: "datetimeoffset" },
  location: { type: "action-icons", name: "location" },
  "file-content": { type: "action-icons", name: "file-content" },
  image: { type: "common", name: "image" }
};

/**
 * The selected row the fill sits on: the deepest selected datasource, action or table header that
 * is showing — a selected action inside an open datasource wins over the datasource, and a header
 * inside a closed or closing folder does not count.
 */
const SELECTED_SOURCE_HEADER_SELECTOR: string =
  ".kuika-folderList__item-selected:not(.rc-collapse-content-inactive *):not(:has(> .rc-collapse-content-active .kuika-folderList__item-selected)) > .rc-collapse-header";

export interface IDataDiagramSchemeWindowState {
  table: IDatabaseTableModel;
  /** An action or table row is being dragged: the selection indicator steps aside until the drop. */
  isRowDragging: boolean;
  isLoading: boolean;
  isSearchActive: boolean;
  /** The header "+"'s connector popover — every create card's list at once. */
  isCreatePopoverOpen: boolean;
  searchText: string;
  datasources: IDatasourceDto[];
  schema: SchemaNode[];
  datasourceActiveKeys: any;
  datasourceLoadingKeys: any;
  datasourceFilteredKeys: string[];
  actionActiveKeys: string[];
  editingFolderId: string;
  editingFolderName: string;
  editingDatasourceId: string;
  folderVisibilityAfterClick: { [key: string]: boolean };
  isHoveredCreateDatabaseMenu: { [key: string]: boolean };
  isNewFolderModalActive: boolean;
  isEditFolderModalActive: boolean;
  actionToDelete: IActionDefinitionDto;
  actionToInspectUsages: IActionDefinitionDto | undefined;
  isActionUsagesModalVisible: boolean;
  restApiEndpointIndexToDelete: number;
  isDeleteSqlActionModalVisible: boolean;
  isDeleteRestApiEndpointModalVisible: boolean;
  datasourceToDelete: IDatasourceDto;
  isDeleteDatasourceModalVisible: boolean;
  isRenaming: boolean;
  renamingId: string;
  isDropdownMenuVisible: { [key: string]: boolean };
  isRenameLoading: boolean;
  isDeleteModalVisible: boolean;
  itemToDelete: any;
  folderParentFolderId: string | null | undefined;
  /** The folder the delete modal is open for, with which of the two folder lists it lives in. */
  folderToDelete: DatasourceFolderRef | null;
  isDeleteFolderModalVisible: boolean;
  /** The table folder the rename dialog is open for, or null while it is closed. */
  tableFolderToRename: ITableFolder | null;
  tableToDelete: IDatabaseTableModel | null;
  isDeleteTableModalVisible: boolean;
  sessionVariables: ISessionVariable[];
  visionActionStatuses: {
    [actionId: string]: "active" | "idle" | "aborted" | "draft";
  };
  visionActionGpuSelections: { [actionId: string]: string };
  isAllActionsFetched: boolean;
}

export interface IDataDiagramSchemeWindowProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  setNextCallbackFunction: (cb: () => void) => void;
}

/** What an inline rename is renaming. */
type RenameKind = "Action" | "Table" | "Datasource";

/** What differs between the tree's three inline rename fields. */
interface IRenameFieldProps {
  /** The action, table or datasource being renamed, handed back to the confirm and key handlers. */
  item: IActionDefinitionDto | IDatabaseTableModel | IDatasourceDto;
  datasource: IDatasourceDto;
  defaultValue: string;
  /** What is being renamed: the list `cancelRenaming` refreshes, and the buttons' names. */
  kind: RenameKind;
  inputTestId?: string;
  confirmTestId?: string;
}

class DataDiagramSchemeWindow extends PureSdComponent<
  IDataDiagramSchemeWindowProps & IWithSDProps,
  IDataDiagramSchemeWindowState
> {
  nameInput: HTMLInputElement | HTMLTextAreaElement | null = null;

  constructor(props: IDataDiagramSchemeWindowProps & IWithSDProps) {
    super(props);
    this.state = {
      table: {} as IDatabaseTableModel,
      isLoading: false,
      isRowDragging: false,
      isSearchActive: false,
      isCreatePopoverOpen: false,
      searchText: "",
      datasources: [],
      schema: [],
      datasourceActiveKeys: [],
      datasourceLoadingKeys: [],
      datasourceFilteredKeys: [],
      actionActiveKeys: [],
      editingFolderId: "",
      editingFolderName: "",
      editingDatasourceId: "",
      folderVisibilityAfterClick: {},
      isHoveredCreateDatabaseMenu: {},
      isNewFolderModalActive: false,
      isEditFolderModalActive: false,
      actionToDelete: {} as IActionDefinitionDto,
      restApiEndpointIndexToDelete: -1,
      actionToInspectUsages: undefined,
      isActionUsagesModalVisible: false,
      isDeleteSqlActionModalVisible: false,
      isDeleteRestApiEndpointModalVisible: false,
      datasourceToDelete: {} as IDatasourceDto,
      isDeleteDatasourceModalVisible: false,
      isRenaming: false,
      renamingId: "",
      isDropdownMenuVisible: {},
      isRenameLoading: false,
      isDeleteModalVisible: false,
      itemToDelete: {} as any,
      folderParentFolderId: "",
      folderToDelete: null,
      isDeleteFolderModalVisible: false,
      tableFolderToRename: null,
      tableToDelete: null,
      isDeleteTableModalVisible: false,
      sessionVariables: [],
      visionActionStatuses: {},
      visionActionGpuSelections: {},
      isAllActionsFetched: false
    };
  }

  /**
   * Browse Data. The data-diagram canvas reads `browsingRowsTable` and shows the table's rows in its
   * main pane — the pane an open table, action or datasource editor holds — so it goes through
   * `handleEditDatasource` the way the table's Edit does: an editor with unsaved changes asks first
   * rather than being torn down under them.
   */
  openTableRowsEditor = (table: IDatabaseTableModel, ds: IDatasourceDto): void => {
    const openTable: IDatabaseTableModel | undefined = this.props.sd?.editingTable;
    // The table already open in the editor: Browse Data only turns it to its Data tab and nothing closes, so
    // there is nothing to guard — parking it would ask about unsaved changes that stay exactly where they are.
    if (openTable && this.props.sd?.creatingTablesDatasource?.id === ds.id && isSameTable(openTable, table)) {
      this.props.sd?.setBrowsingRowsTable(ds, table);
      return;
    }
    this.handleEditDatasource(() => this.props.sd?.setBrowsingRowsTable(ds, table));
  };

  // Designer-live: session variables have no provider store (this panel owns the list),
  // so remote changes arrive as a window event → refetch. Deferred-while-editing is handled
  // upstream (isToolBusy on editingSessionVariables), so a delivery here is always safe.
  handleRemoteSessionVariablesChanged = (): void => {
    this.getSessionVariables();
  };

  componentWillUnmount = () => {
    window.removeEventListener(SESSION_VARIABLES_CHANGED_EVENT, this.handleRemoteSessionVariablesChanged);
  };

  componentDidMount = () => {
    window.addEventListener(SESSION_VARIABLES_CHANGED_EVENT, this.handleRemoteSessionVariablesChanged);
    this.props.sd.updateDatabaseDiagramData();
    this.props.sd.loadActionDefinitions().finally(() => {
      this.setState({
        isAllActionsFetched: true,
        isLoading: false,
        datasourceLoadingKeys: []
      });
    });
    this.props.sd.datasources.map((datasource) => {
      if (
        datasource.datasourceType != EDatasourceType.RestApi &&
        datasource.datasourceType != EDatasourceType.RestApiWOSwagger &&
        datasource.datasourceType != EDatasourceType.CSharpFunctions &&
        datasource.datasourceType != EDatasourceType.CustomAuthenticationProvider &&
        datasource.datasourceType != EDatasourceType.CustomAuthorizationProvider &&
        datasource.datasourceType != EDatasourceType.CustomVerificationCodeSenderProvider &&
        datasource.datasourceType != EDatasourceType.CustomConnectionStringProvider &&
        datasource.datasourceType != EDatasourceType.CustomExternalTokenValidatorProvider &&
        datasource.datasourceType != EDatasourceType.CustomMailProvider &&
        datasource.datasourceType != EDatasourceType.BleService &&
        datasource.datasourceType !== EDatasourceType.Mqtt &&
        datasource.datasourceType !== EDatasourceType.Blockly &&
        !isInlineCodeDatasourceType(datasource.datasourceType) &&
        datasource.datasourceType !== EDatasourceType.AIAgent &&
        datasource.datasourceType !== EDatasourceType.VisionAction &&
        datasource.datasourceType !== EDatasourceType.OData &&
        datasource.datasourceType !== EDatasourceType.SAP
      ) {
        this.props.sd.getTables(datasource.id);
      }
    });
    this.setState({
      datasourceActiveKeys: [this.props.sd?.activeEditingDatasource ? this.props.sd?.activeEditingDatasource.id : ""],
      actionActiveKeys: [`actions_${this.props.sd?.datasources[0]?.id}`]
    });
    this.props.sd.refreshTables();
    if (this.getManagedDB()?.datasourceType == EDatasourceType.ManagedDb) {
      this.getSessionVariables();
    }
    // The Document Parameters node lists the project's definitions, and nothing else on this surface loads them.
    this.props.sd.loadDocumentParameters?.(this.props.sd?.project?.id ?? "");
    this.handleSearchTextOnInit();
  };

  componentDidUpdate = async (prevProps: IDataDiagramSchemeWindowProps & IWithSDProps, prevState) => {
    if (
      prevProps.sd?.lastAddedActionId != this.props.sd?.lastAddedActionId ||
      prevProps.sd?.lastAddedTableId != this.props.sd?.lastAddedTableId
    ) {
      this.setActiveActionTableKey();
      // if (this.actionRef.current) {
      //   this.actionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      // }
    }
    // if (prevProps.sd?.schemas != this.props.sd?.schemas && this.tableRef.current) {
    //   this.tableRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    // }
    if (prevState.isNewFolderModalActive === true && this.state.isNewFolderModalActive === false) {
      await this.props.sd.setDashboardState(DashboardState.readOnly);
      await this.props.sd.setDashboardState(DashboardState.dataDiagramScheme);
    }

    if (
      this.props.sd?.editingSessionVariables === false &&
      prevProps.sd?.editingSessionVariables !== this.props.sd?.editingSessionVariables
    ) {
      this.getSessionVariables();
    }
  };

  getSessionVariables = async () => {
    const response = await DatasourceService.GetSessionVariables();
    const sessionVariables: ISessionVariable[] = response.data;
    if (sessionVariables) {
      this.setState({ sessionVariables });
    }
  };

  handleSearchTextOnInit = () => {
    const searchText = this.props.sd.searchTexts[ViewPart.DataDiagramSchemePanel];
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true });
      this.onSearchChange(searchText);
    }
  };

  showDropdownMenu = (id: string) => {
    this.setState((prevState) => {
      return {
        isDropdownMenuVisible: {
          ...prevState.isDropdownMenuVisible,
          [id]: true
        }
      };
    });
  };

  hideDropdownMenu = (id: string) => {
    this.setState((prevState) => {
      return {
        isDropdownMenuVisible: {
          ...prevState.isDropdownMenuVisible,
          [id]: false
        }
      };
    });
  };

  /** An action folder only: the project does not save table folders, so the tree never makes one. */
  openNewFolderModal = (dsId: string, folderParentFolderId?: string) => {
    this.setState({
      isNewFolderModalActive: true,
      editingDatasourceId: dsId,
      folderParentFolderId: folderParentFolderId ?? null
    });
  };

  closeNewFolderModal = () => {
    this.setState({
      isNewFolderModalActive: false,
      editingDatasourceId: "",
      folderParentFolderId: ""
    });
  };

  openEditFolderModal = (folderId: string, folderName: string, parentFolderId?: string) => {
    this.setState({
      isEditFolderModalActive: true,
      editingFolderId: folderId,
      editingFolderName: folderName,
      folderParentFolderId: parentFolderId ?? null
    });
  };

  closeEditFolderModal = () => {
    this.setState({
      isEditFolderModalActive: false,
      editingFolderId: "",
      editingFolderName: ""
    });
  };

  /** An action folder (top level or sub-folder) or a table folder, from its own list — as the landing does. */
  deleteFolder = (): void => {
    if (this.state.folderToDelete) deleteDatasourceFolder(this.props.sd, this.state.folderToDelete);
  };

  showCreateDatabaseMenuHover = (menuId: string) => {
    this.setState((prevState) => {
      return {
        isHoveredCreateDatabaseMenu: {
          ...prevState.isHoveredCreateDatabaseMenu,
          [menuId]: true
        }
      };
    });
  };

  hideCreateDatabaseMenuHover = (menuId: string) => {
    this.setState((prevState) => {
      return {
        isHoveredCreateDatabaseMenu: {
          ...prevState.isHoveredCreateDatabaseMenu,
          [menuId]: false
        }
      };
    });
  };

  handleEditDatasource(cb: () => void) {
    if (
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName
    ) {
      this.props.setNextCallbackFunction(cb);
      return;
    }
    cb();
  }

  /** The tree's side of the create flows it shares with the Datasources list, behind the same guard as Edit. */
  getTreeActionDeps = (): IDatasourceTreeActionDeps => ({
    sd: this.props.sd,
    showDomainAccessRequiredModal: this.props.showDomainAccessRequiredModal,
    runGuarded: (open: () => void): void => this.handleEditDatasource(open)
  });

  renderDbName = (dbId: string, dbName: string, isManagedDb?: boolean) => {
    const displayName: string = isManagedDb ? "Managed DB" : dbName;
    const datasource: IDatasourceDto | undefined = this.props.sd.datasources.find(
      (ds: IDatasourceDto): boolean => ds.id === dbId
    );
    return (
      <div className="kuika-flex-center--gap kuika__data-diagram__row-label">
        {this.renderDatasourceIcon(datasource)}
        <span
          data-testid={displayName.replace(/\s/g, "") + "db"}
          title={displayName}
          className="kuika__data-diagram__row-name"
        >
          <KuikaOverflowText>{displayName}</KuikaOverflowText>
        </span>
        {/* Provider-type C# datasources are indistinguishable from plain C# here without it —
            same badge as the datasources tab's left panel. */}
        <DatasourceProviderBadge datasource={datasource} />
      </div>
    );
  };

  renderDatabaseHeader = (dbId: string, dbName: string) => {
    const managedDB = this.getManagedDB();
    return this.renderDbName(dbId, dbName, dbId === managedDB?.id);
  };

  /**
   * A datasource row's glyph: the Datasources landing's (`getDatasourceIconName`), so a source reads
   * the same in the tree as in the list beside it. The C# providers are C# files with a role — the
   * badge after the name says which — so they take the C# Functions glyph, which the landing's
   * connector list has no entry of their own for. AI Actions, AI Agents and Vision Actions never
   * reach this tree (see `render`), so they have no case here.
   */
  renderDatasourceIcon = (datasource: IDatasourceDto | undefined): React.ReactNode => {
    if (!datasource) return null;
    if (datasource.connectionString === "" && KMainFunctions.isMainAI()) {
      return <img src="/assets/main-ai/action.svg" alt="" className="kuika__data-diagram__row-icon" />;
    }
    const iconName: string = providerBadgeMap[datasource.datasourceType]
      ? (findConnector(EDatasourceType.CSharpFunctions)?.iconName ?? getDatasourceIconName(datasource))
      : getDatasourceIconName(datasource);
    return (
      <KuikaSvg
        type="datasource-icons"
        name={iconName}
        width={16}
        height={16}
        className="kuika__data-diagram__row-icon"
      />
    );
  };

  /**
   * Renders the header for a datasource action in the data diagram scheme panel.
   * The header includes an icon and a label based on the datasource type.
   *
   * @param {IDatasourceDto} datasource - The datasource object for which to render the header.
   * @returns {JSX.Element} The JSX element representing the header for the datasource action.
   */
  renderDatasourceActionHeader = (datasource) => {
    return (
      <div data-testid="datasourceActionsField" className="kuika__data-diagram__group-name">
        <KuikaSvg name="bolt" width={16} height={16} className="kuika__data-diagram__row-icon" />
        {/* Every datasource renders an "Actions" row with the same id, so the label also carries a
            datasource-scoped one: it is the hover target that reveals the row's add-action "+".
            Scoped by getDatasourceTestIdName like the rest of the tree, so the managed DB is
            "ManagedDB" rather than its display name. */}
        <span data-testid={`datasourceActionsField-${this.getDatasourceTestIdName(datasource.id)}`}>Actions</span>
      </div>
    );
  };

  // Mirrors the datasource-node test-id convention in `renderDbName`, so an E2E
  // test can address the Tables panel (and table rows) of one specific
  // datasource instead of matching the shared "Tables" label text.
  getDatasourceTestIdName = (dbId: string): string => {
    const managedDB: IDatasourceDto | undefined = this.getManagedDB();
    if (dbId === managedDB?.id) {
      return "ManagedDB";
    }
    const datasource: IDatasourceDto | undefined = this.props.sd?.datasources?.find((ds) => ds.id === dbId);
    return (datasource?.name ?? dbId).replace(/\s/g, "");
  };

  // "Tables" stays the div's own text node, beside the glyph: the E2E suite finds the row by
  // `//div[text()='Tables']`.
  renderDatabaseTableHeader = (dbId: string) => {
    return (
      <div
        data-testid={`${this.getDatasourceTestIdName(dbId)}TablesPanelHeader`}
        className="kuika__data-diagram__group-name"
      >
        <KuikaSvg name="table-cells" width={16} height={16} className="kuika__data-diagram__row-icon" />
        Tables
      </div>
    );
  };

  // The name keeps its own `title`d span: the E2E suite finds a folder by `//span[@title=…]`.
  renderFolderHeader = (folderId: string, folderName: string) => {
    return (
      <div key={folderId} className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <KuikaSvg name="folder" type="sidebar-icons" width={16} height={16} className="kuika__data-diagram__row-icon" />
        <span className="kuika__data-diagram__row-name" title={folderName}>
          <KuikaOverflowText>{folderName}</KuikaOverflowText>
        </span>
      </div>
    );
  };

  renderTableHeader = (table: IDatabaseTableModel) => {
    return (
      <div key={table.key} className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <KuikaSvg name="table" width={16} height={16} className="kuika__data-diagram__row-icon" />
        <span data-testid={`${table.name}TableTitle`} title={table.title} className="kuika__data-diagram__row-name">
          <KuikaOverflowText>{table.title}</KuikaOverflowText>
        </span>
      </div>
    );
  };

  // A session variable is a key and its value, not a table: the key glyph the Datasources landing
  // gives the same rows.
  renderSessionVariableHeader = (variable: ISessionVariable) => {
    return (
      <div key={variable.key} className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <KuikaSvg name="key" width={16} height={16} className="kuika__data-diagram__row-icon" />
        <span title={variable.key} className="kuika__data-diagram__row-name">
          <KuikaOverflowText>{variable.key}</KuikaOverflowText>
        </span>
      </div>
    );
  };

  renderActionHeader = (action: any) => {
    // For Vision Actions, parse node count from visionInfo
    const isVisionAction = this.props.sd?.activeEditingDatasource?.datasourceType === EDatasourceType.VisionAction;
    let nodeCount = null;

    if (isVisionAction && action.visionInfo) {
      try {
        const parsed = JSON.parse(action.visionInfo);
        nodeCount = parsed?.nodes?.length || 0;
      } catch (e) {
        // Invalid JSON, ignore
      }
    }

    // One glyph for every action: the tree never lists an AI Agent's or a Vision Action's (their
    // datasources are left out in `render`), which is what the other two glyphs were for.
    return (
      <div className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <KuikaSvg name="lightning" width={16} height={16} className="kuika__data-diagram__row-icon" />
        <span data-testid={action.name} title={action.name} className="kuika__data-diagram__row-name">
          <KuikaOverflowText>{action.name}</KuikaOverflowText>
        </span>
        {nodeCount !== null && <span className="vision-action-node-count">({nodeCount} nodes)</span>}
      </div>
    );
  };

  /**
   * A REST endpoint's row: its method, its name, and its path after the name — one line, like every
   * other row, so it fits the tree's 36px (the name stacked over the path ran 4px past it). The path
   * gives way first; its `title` keeps it whole.
   */
  renderRestApiActionHeader = (action: IActionDefinitionDto, endpoint: IRestApiEndpoint) => {
    const method: string | undefined = endpoint?.operations[0]?.type;
    return (
      <div className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <span className={`kuika_rest-action-header__method ${method}`}>{method}</span>
        <span className="kuika__data-diagram__row-name" title={action.name}>
          <KuikaOverflowText>{action.name}</KuikaOverflowText>
        </span>
        <span className="kuika_rest-action-header__endpoint" title={endpoint.path}>
          <KuikaOverflowText>{endpoint.path}</KuikaOverflowText>
        </span>
      </div>
    );
  };

  // Every row menu below draws its items from `datasource-row-menus.ts`, the list the Datasources
  // landing draws from too. A row whose menu would be empty — no role, or nothing to offer — gets no
  // trigger at all, rather than a "…" or "+" that opens onto nothing.

  datasourceDropdown = (ds: IDatasourceDto) => {
    const options: KuikaDropdownMenuItemType[] = this.datasourceMenuOptions(ds);
    // The managed database has no row menu, and a user without the datasource role sees none.
    if (options.length === 0) return null;

    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton
              isIcon
              type="tertiary"
              color="neutral"
              active={open}
              className="kuika__data-diagram__row-menu"
              data-testid={`${(ds.name ?? "").replace(/\s/g, "")}ThreeDotMenu`}
            >
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  datasourceActionDropdown = (ds: IDatasourceDto) => {
    const options: KuikaDropdownMenuItemType[] = this.datasourceActionsMenuOptions(ds);
    // MQTT and the inline-code runtimes create nothing here.
    if (options.length === 0) return null;
    return (
      // Every datasource renders its own "+" with the same `addActionPlusButton` test-id, so the
      // wrapper carries a datasource-scoped id — it is the only way to address one datasource's
      // add-action button without a structural selector.
      <div
        data-testid={`datasourceAddActionField-${this.getDatasourceTestIdName(ds.id)}`}
        className="kuika__data-diagram__row-controls"
        onClick={(e) => e.stopPropagation()}
      >
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-add">
              <KuikaSvg data-testid="addActionPlusButton" width={16} height={16} name="plus" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  datasourceTableDropdown = (ds: IDatasourceDto) => {
    const options: KuikaDropdownMenuItemType[] = this.datasourceTablesMenuOptions(ds);
    // New tables go into the managed database only.
    if (options.length === 0) return null;
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-add">
              <KuikaSvg data-testid="addTablePlusButton" width={16} height={16} name="plus" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  actionFolderDropdown = (folder: IActionFolder, datasource: IDatasourceDto) => {
    const options: KuikaDropdownMenuItemType[] = this.actionFolderMenuOptions(folder, datasource);
    if (options.length === 0) return null;
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-menu">
              <KuikaSvg data-testid={`${folder.name}ThreeDotMenu`} width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  /**
   * A table folder's "…". It used to be the action-folder menu, whose entries all act on the
   * action-folder list — Rename and Delete found nothing, New Folder and New SQL Action made action
   * things inside a table folder. It now renames and deletes through the table-folder list, the
   * calls the Datasources landing makes; the same test ids as before.
   */
  tableFolderDropdown = (folder: ITableFolder) => {
    const options: KuikaDropdownMenuItemType[] = this.tableFolderMenuOptions(folder);
    if (options.length === 0) return null;
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-menu">
              <KuikaSvg data-testid={`${folder.name}ThreeDotMenu`} width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  tableFolderMenuOptions = (folder: ITableFolder): KuikaDropdownMenuItemType[] => {
    const options: Record<TableFolderMenuItem, KuikaDropdownMenuItemType> = {
      rename: {
        key: "1",
        label: "Rename",
        dataTestId: "renameButton",
        onClick: () => this.setState({ tableFolderToRename: folder })
      },
      delete: {
        key: "3",
        label: "Delete",
        danger: true,
        dataTestId: `${folder.name}DeleteButton`,
        onClick: () => this.openDeleteFolderModal({ folderKind: "table", folder })
      }
    };

    return getTableFolderMenuItems(getDatasourceRowAccess(this.props.sd)).map(
      (item: TableFolderMenuItem): KuikaDropdownMenuItemType => options[item]
    );
  };

  closeTableFolderRename = (): void => {
    this.setState({ tableFolderToRename: null });
  };

  /**
   * The table-folder rename: the folder dialog the tree already uses, committing through the
   * landing's `submitDatasourceFolderDialog` with its sibling-name check. `FolderCreateComponent`
   * renames action folders only.
   */
  renderTableFolderRenameDialog = (): React.ReactNode => {
    const folder: ITableFolder | null = this.state.tableFolderToRename;
    if (!folder) return null;
    const dialog: DatasourceFolderDialog = { mode: "rename", folderKind: "table", folder };
    return (
      <FolderModal
        showEditModal
        closeEditModal={this.closeTableFolderRename}
        editingFolderId={folder.id}
        editingFolderName={folder.name}
        onSubmit={async (values: IFolderFormValues): Promise<boolean> => {
          submitDatasourceFolderDialog(this.props.sd, dialog, values.name);
          return true;
        }}
        checkFolderNameExists={(name: string): boolean =>
          datasourceFolderNameExists(
            dialog,
            this.props.sd.project?.actionFolders ?? [],
            this.props.sd.project?.databaseTablesFolders ?? [],
            name
          )
        }
        folderType="diagram"
      />
    );
  };

  tableMenuDropdown = (table: IDatabaseTableModel, datasource: IDatasourceDto) => {
    const options: KuikaDropdownMenuItemType[] = this.tableMenuOptions(table, datasource);
    // An external server's table offers nothing outside an action's scripting — an MS SQL one used
    // to show a "…" that opened an empty menu.
    if (options.length === 0) return null;
    // Browse Data had a dedicated icon here beside the menu. The menu carries the same item under
    // the same gating, so the row keeps one control instead of two.
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={options} trigger="click">
          {/* The same trigger as every other row's: the kit's icon button, whose `active` keeps it
              on screen while its menu is open — the menu hides with the row otherwise, and a
              trigger that could not say it was open would vanish mid-menu. */}
          {(open: boolean) => (
            <KuikaButton
              isIcon
              type="tertiary"
              color="neutral"
              active={open}
              aria-label="Table actions"
              className="kuika__data-diagram__row-menu"
              data-testid={`${table.name}ThreeDotMenu`}
            >
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  sessionVariableMenuDropdown = () => {
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={this.sessionVariableMenuOptions()} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-menu">
              <KuikaSvg data-testid="sessionVariablesThreeDotMenu" width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  actionMenuDropdown = (action: IActionDefinitionDto, ds: IDatasourceDto, restApiIdx?: number) => {
    const options: KuikaDropdownMenuItemType[] = this.actionMenuOptions(action, ds, restApiIdx);
    // REST (Swagger), C#, the custom providers, the inline-code runtimes, MQTT, Bluetooth and OData
    // edit their actions elsewhere; and a user without the actions role sees no menu.
    if (options.length === 0) return null;
    return (
      <div
        // Same `<name>ThreeDotMenu` shape as the table and folder rows above, so an action's
        // menu can be addressed by test-id instead of an `.ant-collapse-extra` structure walk.
        // It stays a div and the extra's first child: the E2E suite's structural fallback clicks
        // `rc-collapse-extra/div`.
        data-testid={`${action.name}ThreeDotMenu`}
        className="kuika__data-diagram__row-controls"
        onClick={(e) => e.stopPropagation()}
      >
        <KuikaDropdownMenu options={options} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-menu">
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  refreshThirdPartyDbDataSource = (ds: IDatasourceDto) => {
    this.setState({ isLoading: true });
    DatasourceService.GetSchema(ds.id, false, false)
      .then((response) => {
        this.props.sd.refresh3rdPartyDsSchema(ds.id, response.data.tables);
      })
      .catch((e) => {
        KMainFunctions.exceptionHandler(e);
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  datasourceMenuOptions = (ds: IDatasourceDto): KuikaDropdownMenuItemType[] => {
    // Delete is the only entry offered while this datasource is the one being edited, or while
    // one of its actions is.
    const isBeingEdited: boolean =
      this.props.sd.activeEditingDatasource?.id === ds.id || this.props.sd.editingAction?.kDatasource_Id === ds.id;

    const options: Record<DatasourceMenuItem, KuikaDropdownMenuItemType> = {
      edit: {
        key: "2",
        label: "Edit",
        dataTestId: "editbutton",
        onClick: () => this.handleEditDatasource(() => this.props.sd.setActiveEditingDatasource(ds))
      },
      refresh: {
        key: "4",
        label: "Refresh",
        dataTestId: "refreshButton",
        onClick: () => this.refreshThirdPartyDbDataSource(ds)
      },
      rename: {
        key: "1",
        label: "Rename",
        dataTestId: "renameButton",
        onClick: () => this.handleRenaming(ds.id)
      },
      delete: {
        key: "3",
        label: "Delete",
        danger: true,
        dataTestId: "deleteButton",
        onClick: () => this.openDeleteDatasourceModal(ds)
      }
    };

    return getDatasourceMenuItems(ds, getDatasourceRowAccess(this.props.sd), isBeingEdited).map(
      (item: DatasourceMenuItem): KuikaDropdownMenuItemType => options[item]
    );
  };

  getActionNameForCreating = (actions) => {
    actions?.filter((action) => action.name.startsWith("Action"));
    let suffixNumber = 1;

    while (actions?.some((action) => action.name === `Action${suffixNumber}`)) {
      suffixNumber++;
    }

    return `Action${suffixNumber}`;
  };

  datasourceActionsMenuOptions = (ds: IDatasourceDto): KuikaDropdownMenuItemType[] => {
    const options: Record<ActionsGroupMenuItem, KuikaDropdownMenuItemType> = {
      "new-action": {
        key: "1",
        label: `New ${getNewActionKindLabel(ds)} Action`,
        onClick: () => {
          const accessName = "Actions";
          const actions = this.props.sd.actionDefinitions.filter(
            (action) =>
              action.actionType !== EDatasourceType.SystemJSFunctions &&
              action.actionType !== EDatasourceType.SystemCSFunctions
          );
          if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
            this.props.showDomainAccessRequiredModal(accessName);
            return;
          }
          if (ds.datasourceType === EDatasourceType.Blockly) {
            this.props.sd.setEditingActionsDatasource(
              ds,
              undefined,
              true,
              false,
              undefined,
              KMainFunctions.defaultUniqueListElementNameGenerator(
                this.props.sd.actionDefinitions,
                "name",
                "Blockly_Action"
              )
            );
          } else if (isInlineCodeDatasourceType(ds.datasourceType)) {
            this.handleEditDatasource(() => this.props.sd.setActiveEditingDatasource(ds));
          } else {
            this.props.sd.setEditingActionsDatasource(
              ds,
              undefined,
              true,
              false,
              undefined,
              KMainFunctions.defaultUniqueListElementNameGenerator(this.props.sd.actionDefinitions, "name", "Action")
            );
          }
        }
      },
      "new-select-action": {
        key: "3",
        label: "New Select Action",
        dataTestId: "newSelectActionButton",
        onClick: () => {
          const accessName = "Actions";
          const actions = this.props.sd.actionDefinitions.filter(
            (action) =>
              action.actionType !== EDatasourceType.SystemJSFunctions &&
              action.actionType !== EDatasourceType.SystemCSFunctions
          );
          if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
            this.props.showDomainAccessRequiredModal(accessName);
            return;
          }
          const actionNameSuffixNumber = this.getActionNameForCreating(actions);
          this.props.sd.setEditingActionsDatasource(
            ds,
            undefined,
            true,
            false,
            undefined,
            actionNameSuffixNumber,
            true
          );
        }
      },
      "new-rest-endpoint": {
        key: "1",
        label: "New Rest API Endpoint",
        dataTestId: "newRestApiEndpointButton",
        onClick: () =>
          this.handleEditDatasource(() => {
            this.props.sd.setActiveEditingDatasource(ds, -1);
          })
      },
      "new-folder": {
        key: "2",
        label: "New Folder",
        dataTestId: "newFolderButton",
        onClick: () => this.openNewFolderModal(ds.id)
      }
    };

    return getActionsGroupMenuItems(ds, getDatasourceRowAccess(this.props.sd)).map(
      (item: ActionsGroupMenuItem): KuikaDropdownMenuItemType => options[item]
    );
  };

  datasourceTablesMenuOptions = (ds: IDatasourceDto): KuikaDropdownMenuItemType[] => {
    const options: Record<TablesGroupMenuItem, KuikaDropdownMenuItemType> = {
      "new-table": {
        key: "1",
        label: "New Table",
        dataTestId: "newTableButton",
        onClick: () => startNewTable(this.getTreeActionDeps(), ds)
      }
    };

    return getTablesGroupMenuItems(ds, getDatasourceRowAccess(this.props.sd)).map(
      (item: TablesGroupMenuItem): KuikaDropdownMenuItemType => options[item]
    );
  };

  actionMenuOptions = (
    action: IActionDefinitionDto,
    ds: IDatasourceDto,
    restApiIdx?: number
  ): KuikaDropdownMenuItemType[] => {
    // Edit hides while this action — or, for a REST endpoint, this endpoint — is the one open.
    const isOpenInEditor: boolean =
      this.props.sd.activeEditingDatasource?.id === ds.id && this.props.sd.activeEditingDatasourceIndex === restApiIdx;

    const options: Record<ActionMenuItem, KuikaDropdownMenuItemType> = {
      edit: {
        key: "2",
        label: "Edit",
        dataTestId: `${action.name}EditButton`,
        onClick: () => {
          this.handleEditDatasource(() => {
            if (action.actionType === EDatasourceType.Blockly || isInlineCodeDatasourceType(action.actionType)) {
              this.props.sd.setEditingActionsDatasource(ds, action, false, false);
              return;
            }
            if (restApiIdx !== undefined) {
              this.props.sd.setActiveEditingDatasource(ds, restApiIdx);
              return;
            }
            this.props.sd.setEditingActionsDatasource(ds, action, false, false);
          });
        }
      },
      "where-used": {
        key: "4",
        label: "Where Used",
        dataTestId: `${action.name}WhereUsedButton`,
        onClick: () => this.openActionUsagesModal(action)
      },
      delete: {
        key: "3",
        label: "Delete",
        danger: true,
        dataTestId: `${action.name}DeleteButton`,
        onClick: () => {
          if (
            restApiIdx === undefined ||
            ds.datasourceType === EDatasourceType.Blockly ||
            isInlineCodeDatasourceType(ds.datasourceType)
          )
            this.openDeleteSqlActionModal(action);
          else this.openDeleteRestApiEndpointModal(action, ds, restApiIdx);
        }
      }
    };

    return getActionMenuItems({
      datasource: ds,
      access: getDatasourceRowAccess(this.props.sd),
      // Allow editing AI Agents (both custom and template-based) since they have aiAgentInfo;
      // only template-based AI Actions hide Edit.
      isTemplateBased: !canEditAction(this.props.sd, action),
      isOpenInEditor
    }).map((item: ActionMenuItem): KuikaDropdownMenuItemType => options[item]);
  };

  actionFolderMenuOptions = (folder: IActionFolder, ds: IDatasourceDto): KuikaDropdownMenuItemType[] => {
    const createInFolder = (): void => {
      const accessName = "Actions";
      const actions = this.props.sd.actionDefinitions.filter(
        (action) =>
          action.actionType !== EDatasourceType.SystemJSFunctions &&
          action.actionType !== EDatasourceType.SystemCSFunctions
      );
      if (KMainFunctions.isDomainHasAccess(accessName, actions) === false) {
        this.props.showDomainAccessRequiredModal(accessName);
        return;
      }
      const actionNameSuffixNumber = this.getActionNameForCreating(actions);
      this.props.sd.setEditingActionsDatasource(ds, undefined, true, false, folder.id, actionNameSuffixNumber);
    };

    const options: Record<ActionFolderMenuItem, KuikaDropdownMenuItemType> = {
      "new-folder": {
        key: "9",
        label: "New Folder",
        dataTestId: `${folder.name}NewFolderButton`,
        onClick: () => this.openNewFolderModal(folder.id, folder.id)
      },
      rename: {
        key: "1",
        label: "Rename",
        dataTestId: "renameButton",
        onClick: () => this.openEditFolderModal(folder.id, folder.name, folder?.parentFolderId ?? undefined)
      },
      // The test id predates the SAP label; it stays so existing scenarios keep finding the entry.
      "new-action": {
        key: "2",
        label: `New ${getNewActionKindLabel(ds)} Action`,
        dataTestId: `${folder.name}NewSqlActionButton`,
        onClick: createInFolder
      },
      delete: {
        key: "3",
        label: "Delete",
        danger: true,
        dataTestId: `${folder.name}DeleteButton`,
        onClick: () => this.openDeleteFolderModal({ folderKind: "action", folder })
      }
    };

    return getActionFolderMenuItems(folder, ds, getDatasourceRowAccess(this.props.sd)).map(
      (item: ActionFolderMenuItem): KuikaDropdownMenuItemType => options[item]
    );
  };

  tableMenuOptions = (table: IDatabaseTableModel, ds: IDatasourceDto): KuikaDropdownMenuItemType[] => {
    const access: IDatasourceRowAccess = getDatasourceRowAccess(this.props.sd);
    const isEditable: boolean = isTableEditable(this.props.sd, table);
    const isScriptingAction = Boolean(this.props.sd.creatingActionsDatasource || this.props.sd.editingAction);

    const addQuery = (script: string) => () => this.props.sd.setActionScript(table, script, ds.datasourceType);

    const options: Record<TableMenuItem, KuikaDropdownMenuItemType> = {
      // Browse Data (the row-level editor) is managed-DB-only: an external SQL datasource reaches
      // its database through a connection string the designer does not control, and the row
      // endpoints rely on the domain-scoped managed connection.
      browse: {
        key: "browse-data",
        label: "Browse Data",
        dataTestId: `browseDataMenuItem-${table.name}`,
        onClick: () => this.openTableRowsEditor(table, ds)
      },
      edit: {
        key: "2",
        label: "Edit",
        dataTestId: "editTableButton",
        onClick: () => {
          this.collapseFolderAfterMenuClick("2", table.title);
          this.handleEditDatasource(() =>
            this.props.sd?.setEditingTablesDatasource(
              ds,
              this.findTableFromSchema(table),
              false,
              undefined,
              !isEditable
            )
          );
        }
      },
      rename: {
        key: "1",
        label: "Rename",
        dataTestId: "renameTableButton",
        onClick: () => {
          this.collapseFolderAfterMenuClick("1", table.title);
          this.handleRenaming(table.key);
        }
      },
      delete: {
        key: "3",
        label: "Delete",
        danger: true,
        dataTestId: "deleteTableButton",
        onClick: () => this.openDeleteTableModal(table)
      }
    };

    // The query shortcuts write into the action being scripted, so they are this tree's alone.
    const scriptingOptions: KuikaDropdownMenuItemType[] =
      access.tables && isScriptingAction
        ? [
            {
              key: "5",
              label: "Add Select Query",
              dataTestId: "addSelectQueryButton",
              onClick: addQuery("select")
            },
            {
              key: "6",
              label: "Add Select by Id Query",
              dataTestId: "addSelectByIdQueryButton",
              onClick: addQuery("selectById")
            },
            {
              key: "7",
              label: "Add Save Query",
              dataTestId: "addSaveQueryButton",
              onClick: addQuery("update")
            },
            {
              key: "8",
              label: "Add Delete Query",
              dataTestId: "addDeleteQueryButton",
              onClick: addQuery("delete")
            },
            {
              key: "9",
              label: "Add Search by Keyword Query",
              dataTestId: "addSearchByKeywordQueryButton",
              onClick: addQuery("searchByKeyword")
            },
            {
              key: "10",
              label: "Add Search with Pagination Query",
              dataTestId: "addSearchWthPaginationQueryButton",
              onClick: addQuery("searchWithPagination")
            }
          ]
        : [];

    return [
      ...getTableMenuItems(ds, access, isEditable).map(
        (item: TableMenuItem): KuikaDropdownMenuItemType => options[item]
      ),
      ...scriptingOptions
    ];
  };

  sessionVariableMenuOptions = (): KuikaDropdownMenuItemType[] => [
    {
      key: "1",
      label: "Edit",
      dataTestId: "sessionVariablesEditButton",
      onClick: () => {
        this.collapseFolderAfterMenuClick("1", "Session Variables");
        this.handleEditDatasource(() => this.props.sd?.setEditingSessionVariables(true));
      }
    }
  ];

  /**
   * The inline rename that stands in for a row — an action's, a table's, or inside a datasource's
   * header: the field and its confirm/cancel pair, drawn as the house rename draws them
   * (`KuikaEditorTitle`): kit icon buttons, the check in blue, the cross neutral. Enter and Escape
   * do the same from the field.
   */
  renderRenameField = ({
    item,
    datasource,
    defaultValue,
    kind,
    inputTestId,
    confirmTestId
  }: IRenameFieldProps): React.ReactElement => (
    <div onClick={(e) => e.stopPropagation()} className="kuika_renameScreen_input kuika__data-diagram__rename-field">
      <KuikaInput
        size="sm"
        data-testid={inputTestId}
        defaultValue={defaultValue}
        onKeyDown={(e) => {
          this.handleNameKeypress(item, e, datasource);
        }}
        ref={(input) => {
          this.nameInput = input;
        }}
        disabled={this.state.isRenameLoading}
      />
      <KuikaButton
        isIcon
        type="tertiary"
        color="neutral"
        aria-label={`Save ${kind.toLowerCase()} name`}
        data-testid={confirmTestId}
        onClick={() => this.handleNameOnBlur(item, this.nameInput?.value, datasource)}
      >
        <KuikaSvg name="check" width={16} height={16} color="var(--blue-6)" />
      </KuikaButton>
      <KuikaButton
        isIcon
        type="tertiary"
        color="neutral"
        aria-label={`Cancel renaming the ${kind.toLowerCase()}`}
        onClick={() => this.cancelRenaming(kind)}
      >
        <KuikaSvg name="x" width={16} height={16} />
      </KuikaButton>
    </div>
  );

  renderActionsList = (provided, snapshot, action, ds: IDatasourceDto, transform?: any, inFolder?: boolean) => {
    let actionIndex: number | undefined;
    let endpoint: IRestApiEndpoint | undefined;
    if (ds.datasourceType === EDatasourceType.RestApiWOSwagger) {
      // The same lookup the Datasources landing routes its Edit and Delete through.
      actionIndex = findRestEndpointIndex(ds, action);
      endpoint = actionIndex === undefined ? undefined : ds.restApiConnectionRequest?.paths?.[actionIndex];
    } else if (ds.datasourceType === EDatasourceType.Blockly || isInlineCodeDatasourceType(ds.datasourceType)) {
      actionIndex = this.props.sd.actionDefinitions.findIndex((x) => x.id === action.id);
    }

    return this.state.isRenaming && this.state.renamingId === action.id ? (
      this.renderRenameField({ item: action, datasource: ds, defaultValue: action.name, kind: "Action" })
    ) : (
      <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
        <KuikaCollapsePanel
          expandIcon={renderTreeChevron}
          items={[
            {
              label:
                endpoint !== undefined
                  ? this.renderRestApiActionHeader(action, endpoint)
                  : this.renderActionHeader(action),
              key: action.key,
              extra: this.actionMenuDropdown(action, ds, actionIndex),
              className:
                this.props.sd?.editingAction?.name === action.name ||
                (this.props.sd?.lastAddedActionId && this.props.sd?.lastAddedActionId === action.name)
                  ? "kuika-folderList__item-selected"
                  : undefined,
              children: (
                <>
                  <KuikaCollapsePanel
                    expandIcon={renderTreeChevron}
                    items={[
                      {
                        label: <span className="kuika__data-diagram__group-label">Parameters</span>,
                        key: "1",
                        className: inFolder
                          ? "kuika_datasource__action-parameters__in-folder"
                          : "kuika_datasource__action-parameters",
                        children: (
                          <>
                            {action.actionParameters
                              ?.filter(
                                (p) => !(p.name === "runOnServer" && isInlineCodeDatasourceType(ds.datasourceType))
                              )
                              .map((parameter, index) => (
                                <div title={parameter.name} key={index}>
                                  <div className="action-parameter">
                                    <span className="kuika__data-diagram__leaf">
                                      {this.renderTypeGlyph(this.getTypeIcon(parameter.Typename))}
                                      {`${parameter.name}`}
                                    </span>
                                  </div>
                                </div>
                              ))}
                          </>
                        )
                      }
                    ]}
                  />
                </>
              )
            }
          ]}
        />
        {provided.placeholder}
      </div>
    );
  };

  /**
   * One table row, as the `Draggable` it sits in: the row carries the drag props, like an action row
   * does. It used to take the list's droppable props and attach none of them, so no table could be
   * picked up and a filed one broke rbd's ref check.
   */
  renderTablesList = (
    provided: DraggableProvided,
    table: IDatabaseTableModel,
    datasource: IDatasourceDto
  ): React.ReactElement => {
    const isRenaming: boolean = this.state.isRenaming && this.state.renamingId === table.key;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        data-testid={isRenaming ? undefined : `${this.getDatasourceTestIdName(datasource.id)}TableRow-${table.name}`}
      >
        {isRenaming
          ? this.renderRenameField({
              item: table,
              datasource,
              defaultValue: table.title,
              kind: "Table",
              inputTestId: "leftPanelTableRenameInput",
              confirmTestId: "leftPanelConfirmNewTableName"
            })
          : this.renderTableRowPanel(table, datasource)}
      </div>
    );
  };

  renderTableRowPanel = (table: IDatabaseTableModel, datasource: IDatasourceDto): React.ReactNode => {
    return (
      <KuikaCollapsePanel
        expandIcon={renderTreeChevron}
        items={[
          {
            label: this.renderTableHeader(table),
            key: table.key,
            extra: this.tableMenuDropdown(table, datasource),
            className:
              this.props.sd?.editingTable?.name === table.title ||
              (this.props.sd?.lastAddedTableId && this.props.sd?.lastAddedTableId === table.title)
                ? "kuika-folderList__item-selected kuika_datasource__table-columns"
                : "kuika_datasource__table-columns",
            children: (
              <>
                {table.columns
                  // ?.filter((column) => this.filterActionBasedOnIncludesDatasourceName(column, datasource))
                  // Make Id column first
                  ?.sort((a, b) => {
                    if (a.name === "Id") return -1;
                    if (b.name === "Id") return 1;
                    return 0;
                  })
                  ?.map((column, index) => (
                    <div title={column.name} key={column.name}>
                      <div className="table-column">
                        <span className="kuika__data-diagram__leaf">
                          {this.renderTypeGlyph(
                            this.getTypeIconGenericly(datasource.datasourceType, column.sqlDataType)
                          )}
                          {`${column.name}`}
                        </span>
                      </div>
                    </div>
                  ))}
              </>
            )
          }
        ]}
      />
    );
  };

  renderSessionVariableList = (variable: ISessionVariable) => {
    const cell = (text: string) => (
      <div className="table-column">
        <span className="kuika__data-diagram__leaf">{text}</span>
      </div>
    );

    return (
      <div key={variable.key}>
        <KuikaCollapsePanel
          expandIcon={renderTreeChevron}
          items={[
            {
              key: variable.key,
              label: this.renderSessionVariableHeader(variable),
              className: "kuika_datasource__table-columns",
              children: (
                <>
                  {cell(`[key]:${variable.key}`)}
                  {cell(`[value]:${variable.value}`)}
                  {cell(`[isReadOnly]:${variable.isReadOnly}`)}
                </>
              )
            }
          ]}
        />
      </div>
    );
  };

  renderActionPanel = (datasource) => {
    const isLoadingActions = this.state.datasourceLoadingKeys.includes(datasource.id);
    // A datasource that already shows actions or folders keeps them while it reloads; only an
    // empty list gets the placeholder.
    const hasActionRows: boolean =
      (this.props.sd.actionDefinitions ?? []).some(
        (action: IActionDefinitionDto): boolean => action.kDatasource_Id === datasource.id
      ) ||
      (this.props.sd.project.actionFolders ?? []).some(
        (folder: IActionFolder): boolean => folder.dataSourceType === datasource.id
      );
    const actionsPresentation: LoadingPresentationBase = resolveLoadingPresentation(isLoadingActions, hasActionRows);
    return {
      label: this.renderDatasourceActionHeader(datasource),
      key: datasource.id,
      extra: this.datasourceActionDropdown(datasource),
      className: "kuika__data-diagram__database-child-header",
      children: (
        <>
          {/* The rows it is loading, previewed where they will stand — one tree step in, at the
              tree's row height — instead of a full-width band with a spinner in its middle. */}
          {actionsPresentation === "skeleton" && (
            <KuikaSkeletonRegion label="Loading actions" className="kuika__data-diagram__skeleton">
              <KuikaSkeletonList rows={3} density="comfortable" withIcon />
            </KuikaSkeletonRegion>
          )}
          {/* @ts-ignore */}
          <PointerAimedDragDropContext onDragStart={this.handleRowDragStart} onDragEnd={this.handleActionRowDragEnd}>
            {this.props.sd.project.actionFolders
              ?.filter((folder) => {
                if (this.state.searchText) {
                  const selectedDsCustomActions = this.props.sd.actionDefinitions.filter(
                    (x) => x.kDatasource_Id === datasource.id
                  );

                  const doesActionMatch = (actionId) => {
                    const actionX = selectedDsCustomActions.find((action) => action.id === actionId);
                    if (actionX) {
                      const engActionName = KMainFunctions.turkishCharsToEnglish(actionX?.name);
                      return (
                        engActionName.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                        actionX.name.toLowerCase().includes(this.state.searchText.toLowerCase())
                      );
                    }
                    return false;
                  };

                  const engDsName = KMainFunctions.turkishCharsToEnglish(datasource.name);
                  const engFolderName = KMainFunctions.turkishCharsToEnglish(folder.name);

                  if (
                    this.state.datasourceFilteredKeys.length > 0 &&
                    (engDsName.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                      datasource.name.toLowerCase().includes(this.state.searchText.toLowerCase()))
                  )
                    return true;

                  if (
                    engFolderName.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                    folder.name.toLowerCase().includes(this.state.searchText.toLowerCase())
                  )
                    return true;

                  if (folder.actionIds?.some((actionId) => doesActionMatch(actionId))) return true;

                  const childMatches = folder.children?.some((child) => {
                    const engChildName = KMainFunctions.turkishCharsToEnglish(child.name);
                    if (
                      engChildName.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                      child.name.toLowerCase().includes(this.state.searchText.toLowerCase())
                    )
                      return true;

                    return child.actionIds?.some((actionId) => doesActionMatch(actionId));
                  });

                  return childMatches;
                }

                return true;
              })
              // ?.filter((folder) => this.filterActionBasedOnIncludesDatasourceName(folder, datasource))
              ?.sort((a, b) => a.name.localeCompare(b.name))
              ?.map(
                (folder) =>
                  folder.dataSourceType === datasource.id && (
                    // @ts-ignore
                    <PointerAimedDroppable droppableId={folder.id} key={folder.id}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          <KuikaCollapsePanel
                            className={getFolderDropClassName(snapshot)}
                            expandIcon={renderTreeChevron}
                            items={[
                              {
                                label: this.renderFolderHeader(folder.id, folder.name),
                                key: folder.id,
                                extra: this.actionFolderDropdown(folder, datasource),
                                children: (
                                  <>
                                    {folder.actionIds && folder.actionIds.length > 0 ? (
                                      this.props.sd.actionDefinitions
                                        ?.filter((x) => x.kDatasource_Id === datasource.id)
                                        ?.filter((action) =>
                                          this.filterActionBasedOnIncludesDatasourceName(action, datasource)
                                        )
                                        ?.filter((action) => folder.actionIds?.includes(action.id))
                                        ?.map((action, index) => (
                                          // @ts-ignore
                                          <Draggable
                                            key={action.id}
                                            draggableId={action.id}
                                            index={index}
                                            isDragDisabled={
                                              !this.props.sd.project.actionFolders ||
                                              this.props.sd.project.actionFolders?.length === 0 ||
                                              !this.props.sd.project.actionFolders?.some(
                                                (x) => x.dataSourceType === datasource?.id
                                              )
                                            }
                                          >
                                            {(provided: any, snapshot) => {
                                              let { transform } = provided.draggableProps.style;
                                              return this.renderActionsList(
                                                provided,
                                                snapshot,
                                                action,
                                                datasource,
                                                transform,
                                                true
                                              );
                                            }}
                                          </Draggable>
                                        ))
                                    ) : (
                                      <span className="kuika-folder__empty-text">Empty Folder</span>
                                    )}
                                    {folder.children &&
                                      folder.children.length > 0 &&
                                      folder.children.map((childFolder) => (
                                        // @ts-ignore
                                        <PointerAimedDroppable droppableId={childFolder.id} key={childFolder.id}>
                                          {(provided, snapshot) => (
                                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                              <KuikaCollapsePanel
                                                className={getFolderDropClassName(snapshot)}
                                                expandIcon={renderTreeChevron}
                                                items={[
                                                  {
                                                    label: this.renderFolderHeader(childFolder.id, childFolder.name),
                                                    key: childFolder.id,
                                                    extra: this.actionFolderDropdown(childFolder, datasource),
                                                    children: (
                                                      <>
                                                        {childFolder.actionIds && childFolder.actionIds.length > 0 ? (
                                                          this.props.sd.actionDefinitions
                                                            ?.filter((x) => x.kDatasource_Id === datasource.id)
                                                            ?.filter((action) =>
                                                              this.filterActionBasedOnIncludesDatasourceName(
                                                                action,
                                                                datasource
                                                              )
                                                            )
                                                            ?.filter((action) =>
                                                              childFolder.actionIds?.includes(action.id)
                                                            )
                                                            ?.map((action, index) => (
                                                              // @ts-ignore
                                                              <Draggable
                                                                key={action.id}
                                                                draggableId={action.id}
                                                                index={index}
                                                                isDragDisabled={
                                                                  !this.props.sd.project.actionFolders ||
                                                                  this.props.sd.project.actionFolders?.length === 0
                                                                }
                                                              >
                                                                {(provided: any, snapshot) => {
                                                                  let { transform } = provided.draggableProps.style;
                                                                  return this.renderActionsList(
                                                                    provided,
                                                                    snapshot,
                                                                    action,
                                                                    datasource,
                                                                    transform,
                                                                    true
                                                                  );
                                                                }}
                                                              </Draggable>
                                                            ))
                                                        ) : (
                                                          <span className="kuika-folder__empty-text">Empty Folder</span>
                                                        )}
                                                        {provided.placeholder}
                                                      </>
                                                    )
                                                  }
                                                ]}
                                              />
                                              {provided.placeholder}
                                            </div>
                                          )}
                                        </PointerAimedDroppable>
                                      ))}
                                  </>
                                )
                              }
                            ]}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </PointerAimedDroppable>
                  )
              )}
            {/* @ts-ignore */}
            <PointerAimedDroppable droppableId="droppableAction">
              {(provided: any) => (
                // The datasource's own (unfoldered) action list. A collapsed Actions node keeps this
                // out of the layout, so its visibility is the node's expanded state.
                <div
                  data-testid={`datasourceActionsList-${this.getDatasourceTestIdName(datasource.id)}`}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {this.props.sd.actionDefinitions
                    ?.filter((x) => x.kDatasource_Id === datasource.id)
                    ?.filter((action) => this.filterActionBasedOnIncludesDatasourceName(action, datasource))
                    ?.filter((x) => !this.props.sd.project.actionFolders?.find((y) => y.actionIds?.includes(x.id)))
                    ?.filter((x) => {
                      const folder = this.props.sd.findFolderByActionId(x.id, this.props.sd.project.actionFolders);
                      if (folder) {
                        return false;
                      }
                      return true;
                    })
                    ?.sort((a, b) => a.name.localeCompare(b.name))
                    .map((action, index) => (
                      // @ts-ignore
                      <Draggable
                        key={action.id}
                        draggableId={action.id}
                        index={index}
                        isDragDisabled={
                          !this.props.sd.project.actionFolders ||
                          this.props.sd.project.actionFolders?.length === 0 ||
                          !this.props.sd.project.actionFolders?.some((x) => x.dataSourceType === datasource?.id)
                        }
                      >
                        {(provided: any, snapshot) => {
                          let { transform } = provided.draggableProps.style;
                          return this.renderActionsList(provided, snapshot, action, datasource, transform);
                        }}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                </div>
              )}
            </PointerAimedDroppable>
          </PointerAimedDragDropContext>
        </>
      )
    };
  };

  renderSessionVariablesPanel = () => {
    return {
      // A group heading like Actions and Tables, led by the key the Session Variables editor and the
      // Datasources landing name the same thing with.
      label: (
        <div className="kuika__data-diagram__group-name">
          <KuikaSvg name="key" width={16} height={16} className="kuika__data-diagram__row-icon" />
          Session Variables
        </div>
      ),
      key: `session_variables`,
      extra: this.sessionVariableMenuDropdown(),
      className: "kuika__data-diagram__database-child-header",
      children: (
        <>
          {this.state.sessionVariables
            ?.filter(
              (variable) =>
                variable.key.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                variable.value.toLowerCase().includes(this.state.searchText.toLowerCase())
            )
            ?.sort((a, b) => a.key.localeCompare(b.key))
            .map((variable, index) => this.renderSessionVariableList(variable))}
        </>
      )
    };
  };

  /**
   * Document Parameters as a sibling of the datasources (KOA-17254).
   *
   * <b>Why it lives here and not in the "new datasource" dialog.</b> Every entry in that dialog produces actions —
   * databases, APIs, code, AI, devices — and this consumes them, so it is the one thing on the other side of the
   * arrow. This tree is where existing actions are browsed, which is the same mental mode: "what do I have, and
   * where do I wire it".
   *
   * <b>And not under Managed DB, where Session Variables sits.</b> That node is scoped to one datasource; source
   * actions for document parameters can come from any database datasource, so nesting it under one would misstate
   * its reach.
   *
   * <b>Hence the top-level class.</b> It first shipped with `kuika__data-diagram__database-child-header`, copied
   * from Session Variables — the class the datasource's *inner* panels use. So it rendered indented, read as a child
   * of Managed DB, and then failed to disappear when Managed DB was collapsed. Sibling placement has to look like
   * sibling placement, or the collapse behaviour is the thing that gives it away.
   */
  renderDocumentParametersPanel = () => {
    const definitions = this.props.sd?.documentParameterDefinitions ?? [];
    const search = this.state.searchText?.toLowerCase() ?? "";

    return {
      // A top-level row like the datasources beside it, so it takes their glyph-and-name shape; the
      // glyph is the Document Parameters editor's own.
      label: (
        <div className="kuika-flex-center--gap kuika__data-diagram__row-label">
          <KuikaSvg name="file-text" width={16} height={16} className="kuika__data-diagram__row-icon" />
          <span title="Document Parameters" className="kuika__data-diagram__row-name">
            <KuikaOverflowText>Document Parameters</KuikaOverflowText>
          </span>
        </div>
      ),
      key: `document_parameters`,
      extra: this.documentParametersMenuDropdown(),
      className: "kuika__data-diagram__database",
      children: (
        <>
          {definitions
            .filter((definition) => (definition.tagName ?? "").toLowerCase().includes(search))
            .sort((a, b) => (a.tagName ?? "").localeCompare(b.tagName ?? ""))
            .map((definition) => (
              <div
                key={definition.tagName}
                className="kuika_datasource__table-columns"
                data-testid={`documentParameterNode-${definition.tagName}`}
              >
                <div className="table-column">
                  <span className="kuika__data-diagram__leaf">{`<${definition.tagName}>`}</span>
                </div>
              </div>
            ))}
        </>
      )
    };
  };

  documentParametersMenuDropdown = () => {
    return (
      <div className="kuika__data-diagram__row-controls" onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu options={this.documentParametersMenuOptions()} trigger="click">
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open} className="kuika__data-diagram__row-menu">
              <KuikaSvg data-testid="documentParametersThreeDotMenu" width={16} height={16} name="dots" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  documentParametersMenuOptions = (): KuikaDropdownMenuItemType[] => [
    {
      key: "1",
      label: "Edit",
      dataTestId: "documentParametersEditButton",
      onClick: this.openDocumentParameters
    }
  ];

  /**
   * Opens the tool. Navigation only — nothing is loaded on the way.
   *
   * <b>Why nothing is awaited here.</b> This first awaited a full `loadActionDefinitions` before navigating, so the
   * user sat on the old screen with no feedback while every database datasource's schema was re-fetched. Trimming
   * that call was not enough: the tool's own canvas refreshes action definitions on mount, so the pre-load was
   * duplicated work in front of work that was going to happen anyway. The load belongs to the surface that needs it,
   * and it belongs behind the navigation rather than in front of it — which is what the toolbar tools do, since
   * `handleRedirect` never awaits its `extraSetup`.
   *
   * The picker is not empty in the meantime: this panel has already loaded the actions to draw the tree the Edit was
   * clicked in.
   *
   * <b>It is a tool switch, so it leaves the open editor behind (KOA-18653).</b> The editing state is rendered only by
   * the data-diagram canvas, which this tool does not mount, and `handleEditDatasource` parks every later Edit while
   * any of it is set — for an unsaved-changes answer that only the unmounted editor could give. Switching with an
   * editor open therefore left every Edit in the tree dead until a reload. So the switch goes through the same guard
   * as every other Edit here (a dirty editor asks first) and closes the editor on the way, as the toolbar's tool
   * switches do.
   */
  openDocumentParameters = (): void => {
    this.handleEditDatasource((): void => {
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.setDashboardState(DashboardState.documentParameters);
    });
  };

  // The test-id is datasource-scoped because the same schema name (including the
  // `dbo` fallback `groupTablesBySchema` applies) appears under every datasource that has one.
  renderSchemaHeader = (dbId: string, schemaName: string) => {
    return (
      <div className="kuika-flex-center--gap kuika__data-diagram__row-label">
        <span
          data-testid={`${this.getDatasourceTestIdName(dbId)}SchemaHeader-${schemaName}`}
          title={schemaName}
          className="kuika__data-diagram__row-name"
        >
          <KuikaOverflowText>{schemaName}</KuikaOverflowText>
        </span>
      </div>
    );
  };

  /**
   * A table row as a `Draggable`. The draggable id is the id folders file the table under, so a drop
   * reports exactly what the folder stores. Picking one up needs a table folder on the datasource to
   * go into, as an action does; a row being renamed stays put.
   */
  renderTableDraggable = (
    table: IDatabaseTableModel,
    datasource: IDatasourceDto,
    index: number,
    hasTableFolder: boolean
  ): React.ReactNode => {
    const memberId: string = getTableFolderMemberId(table);
    const isRenaming: boolean = this.state.isRenaming && this.state.renamingId === table.key;
    return (
      <Draggable key={memberId} draggableId={memberId} index={index} isDragDisabled={!hasTableFolder || isRenaming}>
        {(provided: DraggableProvided): React.ReactElement => this.renderTablesList(provided, table, datasource)}
      </Draggable>
    );
  };

  renderTablePanel = (datasource) => {
    const tables: IDatabaseTableModel[] = this.props.sd?.schemas.get(datasource.id)?.tables ?? [];
    // Only this datasource's folders: a member id is unique within a datasource, not across them.
    const tableFolders: ITableFolder[] = (this.props.sd.project.databaseTablesFolders ?? []).filter(
      (folder: ITableFolder): boolean => folder.dataSourceType === datasource.id
    );
    const filedIds: Set<string> = new Set<string>(
      tableFolders.flatMap((folder: ITableFolder): string[] => folder.tableIds ?? [])
    );
    const hasTableFolder: boolean = tableFolders.length > 0;
    const tablesNotInFolders = tables
      .filter(
        (table) =>
          this.filterTableBasedOnIncludesDatasourceName(table, datasource) &&
          !filedIds.has(getTableFolderMemberId(table))
      )
      .sort((a, b) => a.title.localeCompare(b.title));

    const groupedBySchema = groupTablesBySchema(tablesNotInFolders);
    const schemaNames = Array.from(groupedBySchema.keys()).sort();

    return {
      label: this.renderDatabaseTableHeader(datasource.id),
      key: `tables_${datasource.id}`,
      extra: this.datasourceTableDropdown(datasource),
      className: "kuika__data-diagram__database-child-header",
      children: (
        <>
          {/* @ts-ignore */}
          <PointerAimedDragDropContext onDragStart={this.handleRowDragStart} onDragEnd={this.handleTableRowDragEnd}>
            {/* @ts-ignore */}
            {schemaNames.map((schemaName) => (
              <KuikaCollapsePanel
                key={schemaName}
                expandIcon={renderTreeChevron}
                defaultActiveKey={[`schema_panel_${schemaName}_${datasource.id}`]}
                items={[
                  {
                    label: this.renderSchemaHeader(datasource.id, schemaName),
                    key: `schema_panel_${schemaName}_${datasource.id}`,
                    className: "kuika__data-diagram__schema-panel",
                    children: (
                      <>
                        {/* One droppable per schema: they share a drag context, where an id names one list. */}
                        <PointerAimedDroppable droppableId={getUnfiledTablesDroppableId(schemaName)}>
                          {(provided: DroppableProvided): React.ReactElement => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {groupedBySchema
                                .get(schemaName)
                                ?.sort((a, b) => a.title.localeCompare(b.title))
                                .map((table: IDatabaseTableModel, index: number) =>
                                  this.renderTableDraggable(table, datasource, index, hasTableFolder)
                                )}
                              {provided.placeholder}
                            </div>
                          )}
                        </PointerAimedDroppable>
                      </>
                    )
                  }
                ]}
              />
            ))}
            {tableFolders
              .filter((folder) => {
                if (this.state.searchText) {
                  return folder.tableIds?.some((tableId) => {
                    const tableX: IDatabaseTableModel | undefined = tables.find(
                      (table: IDatabaseTableModel): boolean => getTableFolderMemberId(table) === tableId
                    );
                    const engFolderName = KMainFunctions.turkishCharsToEnglish(folder.name);
                    if (
                      engFolderName.toLowerCase().includes(this.state.searchText.toLowerCase()) ||
                      folder.name.toLowerCase().includes(this.state.searchText.toLowerCase())
                    )
                      return true;
                    if (tableX) {
                      const engTableName = KMainFunctions.turkishCharsToEnglish(tableX?.title);
                      const isTableInFolderEng: boolean = engTableName
                        .toLowerCase()
                        .includes(this.state.searchText.toLowerCase());
                      const isTableInFolder: boolean = tableX?.title
                        .toLowerCase()
                        .includes(this.state.searchText.toLowerCase());
                      if (isTableInFolderEng || isTableInFolder) return true;
                      return false;
                    }
                    return false;
                  });
                }
                return true;
              })
              .map((folder: ITableFolder): React.ReactNode => {
                // What the folder holds among the tables read now. An id that matches none — a table
                // since dropped, or a per-read key an older build filed — is not a row.
                const folderTables: IDatabaseTableModel[] = tables
                  .filter((table) => this.filterTableBasedOnIncludesDatasourceName(table, datasource))
                  .filter((table: IDatabaseTableModel): boolean =>
                    (folder.tableIds ?? []).includes(getTableFolderMemberId(table))
                  );
                return (
                  <PointerAimedDroppable droppableId={folder.id} key={folder.id}>
                    {(provided: DroppableProvided, snapshot: DroppableStateSnapshot): React.ReactElement => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        <KuikaCollapsePanel
                          className={getFolderDropClassName(snapshot)}
                          expandIcon={renderTreeChevron}
                          items={[
                            {
                              label: this.renderFolderHeader(folder.id, folder.name),
                              key: folder.id,
                              extra: this.tableFolderDropdown(folder),
                              children: (
                                <>
                                  {folderTables.length > 0 ? (
                                    folderTables.map((table: IDatabaseTableModel, index: number) =>
                                      this.renderTableDraggable(table, datasource, index, hasTableFolder)
                                    )
                                  ) : (
                                    <span className="kuika-folder__empty-text">Empty Folder</span>
                                  )}
                                </>
                              )
                            }
                          ]}
                        />
                        {provided.placeholder}
                      </div>
                    )}
                  </PointerAimedDroppable>
                );
              })}
          </PointerAimedDragDropContext>
        </>
      )
    };
  };

  deleteSearchText = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    this.props.sd.setSearchTexts(ViewPart.DataDiagramSchemePanel, "");
    this.setState({
      searchText: "",
      datasourceFilteredKeys: [],
      actionActiveKeys: [],
      isSearchActive: false
    });
  };

  deleteTable = async (tableSchema) => {
    const table = this.props.sd.databaseDiagramData.leftMenuData?.tables.find((t) => t.name === tableSchema.title);
    if (table) {
      const offlineTable = this.props.sd.offlineTables.find((ot) => ot.tableName === table.name);
      try {
        const tableObj = {} as IDatabaseTableModel;
        tableObj.id = table.id;
        tableObj.name = table.name;
        tableObj.schema = table.schema;
        tableObj.dropped = true;
        tableObj.columns = [];

        const final = {} as IDataDiagramSchemeDto;
        final.tables = [tableObj];
        const promises = [
          DataDiagramSchemeService.ExecuteMigrationScript(final),
          offlineTable ? OfflineTablesService.delete(table.name) : Promise.resolve({ status: 200 })
        ];
        const results = await Promise.allSettled(promises);
        if (results[0].status === "rejected") throw results[0].reason;
        if (results[1].status === "rejected") throw results[1].reason;
        const deleteTableReq: any = results[0].value;
        if (deleteTableReq.status === 200) {
          if (this.props.sd.editingTable && this.props.sd.editingTable.id === table.id) {
            this.props.sd.cancelCreatingOrEditingDatasource();
            this.props.sd.setCreateNotificationTemplateActive();
          }
          this.props.sd.refreshTables();
          this.props.sd.updateDatabaseDiagramData();
          KMainFunctions.displaySuccessNotification("Table is deleted successfully");
          const index = this.state.schema.findIndex((t) => t.title === tableSchema.title);
          if (index > -1) {
            this.state.schema.splice(index, 1);
            this.setState({ schema: this.state.schema });
          }
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      }
    }
  };

  handleLoadDatasourceActions = async (keys: string[]) => {
    if (!keys || keys.length === 0) return;

    try {
      this.setState((prevState) => ({
        datasourceLoadingKeys: [...prevState.datasourceLoadingKeys, ...keys]
      }));

      const requestList: Promise<AxiosResponse<IActionDefinitionDto[]>>[] = [];

      keys.forEach((key) => {
        const datasource = this.props.sd.datasources.find((ds) => ds.id === key);
        if (!datasource) return;

        requestList.push(DatasourceService.GetDatasourceActions(key));
      });

      const responses = await Promise.allSettled(requestList);

      let freshActions: IActionDefinitionDto[] = [];

      keys.forEach((_key, i) => {
        if (!responses[i]) return;

        if (responses[i].status === "rejected") {
          throw responses[i].reason;
        }
        freshActions = freshActions.concat(responses[i].value.data);
      });

      const updatedDatasourceActions = [
        ...this.props.sd.actionDefinitions.filter((action) => !keys.includes(action.kDatasource_Id)),
        ...freshActions
      ];
      this.props.sd.updateActionDefinitions(updatedDatasourceActions);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState((prevState) => ({
        datasourceLoadingKeys: prevState.datasourceLoadingKeys.filter((loadingKey) => !keys.includes(loadingKey))
      }));
    }
  };

  onDatasourcePanelChange = (key: string | string[]) => {
    let keys = typeof key === "string" ? [key] : key;
    const previousKeys: string[] = this.state.datasourceActiveKeys;
    const newlyExpandedKeys = keys.filter((k) => !previousKeys.includes(k));

    this.setState({ datasourceActiveKeys: key, datasourceFilteredKeys: keys }, () => {
      if (this.state.isAllActionsFetched) return;

      this.handleLoadDatasourceActions(newlyExpandedKeys);
    });
  };

  onActionPanelChange = (key) => {
    // if (key?.includes("actions") || key?.includes("tables")) {
    //   this.setState({ datasourceFilteredKeys: key });
    // }
    this.setState({ actionActiveKeys: key });
  };

  // The kit calls a menu entry's own handler, so the key arrives directly instead of being
  // read off antd's menu event. Same rule: the two entries that open an editor fold the group.
  collapseFolderAfterMenuClick = (key: string, tableId: string) => {
    this.handleFolderMenuClick({ key }, tableId);
  };

  handleFolderMenuClick = (e, tableId) => {
    if (e.key === "1" || e.key === "2") {
      this.setState((prevState) => {
        return {
          folderVisibilityAfterClick: {
            ...prevState.folderVisibilityAfterClick,
            [tableId]: false
          }
        };
      });
    }
  };

  handleRowDragStart = (): void => {
    this.setState({ isRowDragging: true });
  };

  handleActionRowDragEnd = (result: DropResult): void => {
    this.setState({ isRowDragging: false });
    this.onActionDragEnd(result);
  };

  handleTableRowDragEnd = (result: DropResult): void => {
    this.setState({ isRowDragging: false });
    this.onTableDragEnd(result);
  };

  onActionDragEnd = (result: any) => {
    const { source, destination } = result;
    const folder = this.props.sd.getFolderById(
      destination?.droppableId !== "droppableAction" && destination?.droppableId
        ? destination?.droppableId
        : source.droppableId,
      this.props.sd.project.actionFolders
    );
    if (!destination || destination?.droppableId === "droppableAction") {
      if (folder?.parentFolderId) {
        this.props.sd.removeActionFromFolderInFolder(folder.parentFolderId, folder.id, result.draggableId);
      } else {
        this.props.sd.removeActionFromFolder(source.droppableId, result.draggableId);
      }
    } else if (folder?.parentFolderId) {
      this.props.sd.addActionToFolderInFolder(folder.parentFolderId, destination?.droppableId, result.draggableId);
    } else {
      this.props.sd.addActionToFolder(destination?.droppableId, result.draggableId);
    }
  };

  /** The same move the Datasources landing makes, resolved from this tree's droppable ids. */
  onTableDragEnd = (result: DropResult): void => {
    if (result.reason === "CANCEL") return;
    const move: DatasourceRowMove | null = resolveSourcesTreeTableDrop({
      draggableId: result.draggableId,
      sourceDroppableId: result.source.droppableId,
      destinationDroppableId: result.destination?.droppableId
    });
    if (!move) return;
    applyDatasourceRowMove(this.props.sd, move);
  };

  findTableFromSchema = (schema: IDatabaseTableModel) => {
    const table: IDatabaseTableModel | undefined = this.props.sd.databaseDiagramData.leftMenuData?.tables.find(
      (t) => t.name === schema.title && schema.schema === t.schema
    );
    return table;
  };

  getManagedDB = () => {
    const managedDB = this.props.sd?.datasources?.find((d) => d.connectionString == "");
    if (managedDB) {
      return managedDB;
    }
  };

  /**
   * Whether the ER diagram is what the centre shows — the one case the managed database's row is
   * the selected row. An idle Datasources centre used to BE the diagram, so "nothing is being
   * edited" was enough; it is the landing now, the diagram a view the user asks for, and the tree
   * also sits beside tools with no diagram at all (Scheduled Jobs, the API Builder).
   */
  isDatabaseDiagramActive = (): boolean => {
    if (this.props.sd?.dashboardState !== DashboardState.dataDiagramScheme) return false;
    if (this.props.sd?.datasourcesCentreView !== "diagram") return false;
    if (
      this.props.sd?.editingTable ||
      this.props.sd?.creatingTablesDatasource ||
      this.props.sd?.activeCreatingDatasourceType ||
      this.props.sd?.editingAction ||
      this.props.sd?.creatingActionsDatasource ||
      this.props.sd?.activeEditingDatasource
    ) {
      return false;
    }
    return true;
  };

  setActiveActionTableKey = () => {
    let { actionActiveKeys } = this.state;
    let newActionActiveKeys = [...actionActiveKeys];

    if (this.props.sd.editingTable) {
      newActionActiveKeys = [this.props.sd.editingTable.kDatasource_Id];
    } else if (this.props.sd.editingAction) {
      newActionActiveKeys = [this.props.sd.editingAction.kDatasource_Id];
    } else if (this.props.sd.lastAddedActionDsId) {
      newActionActiveKeys = [`actions_${this.props.sd.lastAddedActionDsId}`];
    } else if (this.props.sd.lastAddedTableDsId && this.props.sd.lastAddedTableId) {
      newActionActiveKeys = [`tables_${this.props.sd.lastAddedTableDsId}`];
    }

    if (!_.isEqual(actionActiveKeys, newActionActiveKeys)) {
      this.setState({ actionActiveKeys: newActionActiveKeys });
    }
  };

  handleRenaming = (id: string) => {
    // this.hideApiIcon(id);
    this.setState({ renamingId: id, isRenaming: true }, () => {
      this.nameInput?.focus();
    });
  };

  handleNameKeypress = async (item: any, e: any, datasource: IDatasourceDto) => {
    try {
      if (e.keyCode === 13) {
        this.handleNameOnBlur(item, e.target?.value, datasource);
      } else if (e.keyCode === 27) {
        this.cancelRenaming();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  cancelRenaming = (type?: RenameKind): void => {
    if (type === "Datasource") {
      this.props.sd.refreshDatabases();
    } else if (type === "Action") {
      this.props.sd.refreshActions();
    } else if (type === "Table") {
      this.props.sd.refreshTables();
    }
    this.setState({ isRenaming: false, renamingId: "" });
  };

  handleNameOnBlur = async (item: any, newName: string | undefined, datasource?: IDatasourceDto) => {
    // A missing value reads as an empty name, which changeName already reports to the user.
    this.changeName(item, newName ?? "", datasource);
  };

  changeName = (item: any, newName: string, datasource?: IDatasourceDto) => {
    this.setState({ isRenameLoading: true });
    const regex = /^[A-Za-z][A-Za-z0-9_]+$/;
    const isNameValid = regex.test(newName);
    let type;
    if (item.datasourceType) {
      type = "Datasource";
    } else if (item.actionTemplate) {
      type = "Action";
    } else if (item.columns) {
      type = "Table";
    }

    if (!newName) {
      KMainFunctions.displayWarningNotification(`Please enter a name for the ${type}.`);
      this.setState({ isRenameLoading: false });
      return;
    }
    if (!isNameValid) {
      KMainFunctions.displayWarningNotification(`${type} name is invalid.`);
      this.setState({ isRenameLoading: false });
      return;
    }

    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === newName.toLowerCase()
    );
    const isEqualToWfName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === newName.toLowerCase());
    if (isEqualToScreenName || (isEqualToWfName && type === "Datasource")) {
      KMainFunctions.displayWarningNotification(`${type} name can not be equal to screen name or process name.`);
      this.setState({ isRenameLoading: false });
      return;
    }

    // Check if datasource name matches any of its child action names
    if (type === "Datasource") {
      const datasourceActions = this.props.sd.actionDefinitions.filter((action) => action.kDatasource_Id === item.id);
      const isEqualToActionName = datasourceActions?.find(
        (action: any) => action.name?.toLowerCase() === newName.toLowerCase()
      );
      if (isEqualToActionName) {
        KMainFunctions.displayWarningNotification("Datasource name cannot be the same as its child actions' name.");
        this.setState({ isRenameLoading: false });
        return;
      }
    }

    let obj = {} as any;
    obj = item;
    if (type !== "Table") {
      obj.name = newName;
    }
    this.upddate(obj, type, newName, datasource);
  };

  upddate = async (item: any, type: string, newName: string, ds?: IDatasourceDto) => {
    if (type === "Datasource") {
      const isEqualToScreenName = this.props.sd?.screenList?.find(
        (screen) => screen.name.toLowerCase() === newName?.toLowerCase()
      );
      const isEqualToWfName = this.props.sd?.workFlowList?.find(
        (wf) => wf.name.toLowerCase() === newName?.toLowerCase()
      );
      if (isEqualToScreenName || isEqualToWfName) {
        this.setState({ isRenameLoading: false });
        KMainFunctions.displayWarningNotification("Datasource name can not be equal to screen name or process name.");
        return;
      }
      try {
        const res = await DatasourceService.SaveDatasource(item);
        if (res.status === 200) {
          this.props.sd.refreshDatabases();
          this.props.sd.refreshActions();
          KMainFunctions.displaySuccessNotification(`${type} name updated successfully.`);
          this.cancelRenaming();
        }
      } catch (err) {
        KMainFunctions.exceptionHandler(err);
      } finally {
        this.setState({ isRenameLoading: false });
      }
    } else if (type === "Action") {
      item.parameters = item.actionParameters;
      try {
        const res = await DatasourceService.SaveAction(item);
        if (res.status === 200) {
          this.props.sd.loadActionDefinitions();
          this.props.sd.refreshActions();
          this.handleEditDatasource(() => this.props.sd.setEditingActionsDatasource(ds, item, false, false));
          KMainFunctions.displaySuccessNotification(`${type} name updated successfully.`);
          this.cancelRenaming();
        }
      } catch (err) {
        KMainFunctions.exceptionHandler(err);
      } finally {
        this.setState({ isRenameLoading: false });
      }
    } else if (type === "Table") {
      const final = {} as IDataDiagramSchemeDto;
      const tableObj = {} as IDatabaseTableModel;
      const table: IDatabaseTableModel | undefined = this.props.sd.databaseDiagramData.leftMenuData?.tables.find(
        (t) => t.name === item.title
      );
      const isTableExist = this.props.sd.databaseDiagramData.leftMenuData?.tables?.find(
        (x) => x.name.toLowerCase() === newName.toLowerCase() && x.id !== table?.id
      );
      if (isTableExist) {
        KMainFunctions.displayWarningNotification("Table name already exist.");
        this.setState({ isRenameLoading: false });
        return;
      }
      if (table) {
        tableObj.id = table.id;
        tableObj.name = newName;
        tableObj.schema = table.schema;
        tableObj.dropped = table.dropped;
        tableObj.columns = table.columns;

        final.tables = [tableObj];

        try {
          const res = await DataDiagramSchemeService.ExecuteMigrationScript(final);
          if (res.status === 200) {
            this.props.sd.refreshTables();
            this.props.sd.updateDatabaseDiagramData();
            this.props.sd.setEditingTablesDatasource(ds, tableObj, false);
            KMainFunctions.displaySuccessNotification(`${type} name updated successfully.`);
            this.cancelRenaming();
          }
        } catch (err) {
          KMainFunctions.exceptionHandler(err);
        } finally {
          this.setState({ isRenameLoading: false });
        }
      }
    }
  };

  triggerDeleteModal = (e: any, item: any) => {
    e.stopPropagation();
    this.hideDropdownMenu(item.id);
    this.setState({ isDeleteModalVisible: true, itemToDelete: item });
  };

  onSearchChange = (searchText: string) => {
    this.setState({ searchText, isLoading: !this.state.isAllActionsFetched });
    if (this.state.datasourceFilteredKeys) {
      const activeActionKeys = this.state.actionActiveKeys;
      this.state.datasourceFilteredKeys.forEach((dsId) => {
        if (!activeActionKeys.includes(dsId)) {
          activeActionKeys.push(`actions_${dsId}`);
          activeActionKeys.push(`tables_${dsId}`);
        }
      });
    }
    if (searchText?.length === 0) {
      this.setState({ datasourceFilteredKeys: [], actionActiveKeys: [] });
    }
    this.props.sd.setSearchTexts(ViewPart.DataDiagramSchemePanel, searchText);
  };

  filterActionBasedOnIncludesDatasourceName = (x, datasource) => {
    const engDsName = KMainFunctions.turkishCharsToEnglish(datasource.name);
    const engActName = KMainFunctions.turkishCharsToEnglish(x.name);
    if (
      datasource.datasourceType !== EDatasourceType.ManagedDb &&
      datasource.datasourceType !== EDatasourceType.ManagedDbOracle &&
      datasource.datasourceType !== EDatasourceType.ManagedDbPostgre &&
      this.state.datasourceFilteredKeys.length > 0 &&
      (engDsName.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1 ||
        datasource.name.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1)
    ) {
      return true;
    }
    const engActionName = engActName.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1;
    const actionName = x.name.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1;
    if (engActionName || actionName) return true;
    return false;
  };

  filterTableBasedOnIncludesDatasourceName = (x, datasource) => {
    const engDsName = KMainFunctions.turkishCharsToEnglish(datasource.name);
    const engTableName = KMainFunctions.turkishCharsToEnglish(x.title);
    if (
      datasource.datasourceType !== EDatasourceType.ManagedDb &&
      datasource.datasourceType !== EDatasourceType.ManagedDbOracle &&
      datasource.datasourceType !== EDatasourceType.ManagedDbPostgre &&
      this.state.datasourceFilteredKeys.length > 0 &&
      (engDsName?.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1 ||
        datasource.name.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1)
    ) {
      return true;
    }
    const engTable = engTableName?.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1;
    const table = x.title?.toLowerCase().indexOf(this.state.searchText.toLowerCase()) !== -1;
    if (engTable || table) return true;
    return false;
  };

  checkSearch = (datasource) => {
    if (this.state.searchText.length > 0) {
      const selectedDsCustomActions = this.props.sd.actionDefinitions.filter((x) => x.kDatasource_Id === datasource.id);
      const selectedDsTables: any = this.props.sd?.schemas?.get(datasource.id)?.tables;
      const selectedDsColumns: any = selectedDsTables?.flatMap((x) => x.columns);
      const selectedDsActionFolders = this.props.sd.project.actionFolders;
      const engName = KMainFunctions.turkishCharsToEnglish(datasource.name);
      if (
        engName?.toLowerCase().includes(this.state.searchText?.toLowerCase()) ||
        datasource.name?.toLowerCase().includes(this.state.searchText?.toLowerCase())
      ) {
        this.state.datasourceFilteredKeys.push(datasource.id);
        return true;
      }
      if (selectedDsCustomActions || selectedDsTables || selectedDsColumns || selectedDsActionFolders) {
        const isActionExist: boolean = selectedDsCustomActions?.some((action) => {
          const engActName = KMainFunctions.turkishCharsToEnglish(action?.name);
          if (
            engActName?.toLowerCase().includes(this.state.searchText?.toLowerCase()) ||
            action?.name?.toLowerCase().includes(this.state.searchText?.toLowerCase())
          ) {
            this.state.datasourceFilteredKeys.push(datasource.id);
            return true;
          }
        });
        const isTableExist: boolean = selectedDsTables?.some((table) => {
          const engTableName = KMainFunctions.turkishCharsToEnglish(table?.title);
          if (
            engTableName?.toLowerCase().includes(this.state.searchText?.toLowerCase()) ||
            table?.title?.toLowerCase().includes(this.state.searchText?.toLowerCase())
          ) {
            this.state.datasourceFilteredKeys.push(datasource.id);
            return true;
          }
        });
        // let isColumnExist: boolean = selectedDsColumns?.some((column) => {
        //   if (column?.name.toLowerCase().includes(this.state.searchText.toLowerCase())) {
        //     this.state.datasourceFilteredKeys.push(datasource.id);
        //     return true;
        //   }
        // });
        if (isActionExist) return isActionExist;
        if (isTableExist) return isTableExist;
        // else if(isColumnExist) return isColumnExist;
        return false;
      }
    } else return true;
  };

  openDeleteRestApiEndpointModal = (action: IActionDefinitionDto, ds: IDatasourceDto, index: number) => {
    this.setState({
      actionToDelete: action,
      datasourceToDelete: ds,
      restApiEndpointIndexToDelete: index,
      isDeleteRestApiEndpointModalVisible: true
    });
  };

  openActionUsagesModal = (action: IActionDefinitionDto) => {
    this.setState({ actionToInspectUsages: action, isActionUsagesModalVisible: true });
  };

  openDeleteSqlActionModal = (action) => {
    this.setState({
      actionToDelete: action,
      isDeleteSqlActionModalVisible: true
    });
  };

  openDeleteDatasourceModal = (ds) => {
    this.setState({
      datasourceToDelete: ds,
      isDeleteDatasourceModalVisible: true
    });
  };

  openDeleteFolderModal = (folder: DatasourceFolderRef): void => {
    this.setState({ folderToDelete: folder, isDeleteFolderModalVisible: true });
  };

  // Dropping a table is the one delete in this tree that used to ask inside the menu, with a
  // confirm bubble nested in the entry. It asks the way its siblings do now.
  openDeleteTableModal = (table: IDatabaseTableModel) => {
    this.setState({ tableToDelete: table, isDeleteTableModalVisible: true });
  };

  deleteSqlAction = async () => {
    try {
      const res = await DatasourceService.DeleteAction(this.state.actionToDelete);

      if (res && res.status === 200) {
        if (this.props.sd.editingAction && this.props.sd.editingAction.id === this.state.actionToDelete.id) {
          this.props.sd.cancelCreatingOrEditingDatasource();
          this.props.sd.setCreateNotificationTemplateActive();
        }

        this.props.sd.loadActionDefinitions();
        this.props.sd.refreshActions();
        KMainFunctions.displaySuccessNotification("Action deleted successfully");
      }
    } catch (error) {
      KMainFunctions.displayErrorNotification(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  deleteRestApiEndpoint = async () => {
    try {
      const { datasourceToDelete, restApiEndpointIndexToDelete, actionToDelete } = _.cloneDeep(this.state);
      if (!datasourceToDelete || restApiEndpointIndexToDelete < -1) return;
      this.setState({ isLoading: true });
      const deleterRes = await DatasourceService.DeleteAction(actionToDelete);
      if (!deleterRes || deleterRes.status !== 200) throw new Error();

      const paths = datasourceToDelete.restApiConnectionRequest.paths;
      if (!paths?.length) throw new Error("Rest API Datasource Endpoints are not found");
      paths.splice(restApiEndpointIndexToDelete, 1);
      const res = await DatasourceService.SaveDatasource(datasourceToDelete);
      if (!res || res.status !== 200) throw new Error("Something went wrong while deleting the Rest API Endpoint");
      if (
        this.props.sd.activeEditingDatasource &&
        this.props.sd.activeEditingDatasourceIndex !== undefined &&
        this.props.sd.activeEditingDatasource.id === datasourceToDelete.id &&
        this.props.sd.activeEditingDatasourceIndex === restApiEndpointIndexToDelete
      ) {
        this.props.sd.cancelCreatingOrEditingDatasource();
        this.props.sd.setCreateNotificationTemplateActive();
      }
      this.props.sd.loadActionDefinitions();
      this.props.sd.refreshActions();
      KMainFunctions.displaySuccessNotification("Rest API Enpoint is deleted successfully.");
    } catch (err) {
      KMainFunctions.exceptionHandler(err);
    } finally {
      this.setState({
        isLoading: false,
        datasourceToDelete: {} as IDatasourceDto,
        restApiEndpointIndexToDelete: -1
      });
    }
  };

  deleteDatasource = async () => {
    if (this.state.datasourceToDelete && this.state.datasourceToDelete?.id) {
      this.setState({ isLoading: true });
      try {
        const res = await DatasourceService.DeleteDatasource(this.state.datasourceToDelete.id);
        if (res && res.status === 200) {
          if (
            this.props.sd.activeEditingDatasource &&
            this.props.sd.activeEditingDatasource.id === this.state.datasourceToDelete.id
          ) {
            this.props.sd.cancelCreatingOrEditingDatasource();
            this.props.sd.setCreateNotificationTemplateActive();
          }
          this.props.sd.refreshDatabases();
          this.props.sd.refreshActions();
          KMainFunctions.displaySuccessNotification("Datasource deleted successfully.");
        }
      } catch (error) {
        KMainFunctions.displayErrorNotification(error);
      } finally {
        this.setState({ isLoading: false });
      }
    }
  };

  /**
   * The "+" is its popover's trigger, but it opens the popover itself rather than letting the
   * trigger toggle it: `preventDefault` here is what stops Radix's own toggle, so an open editor
   * gets its unsaved-changes prompt first and the popover only appears once that is settled.
   */
  handleCreateOnClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    if (this.state.isCreatePopoverOpen) {
      this.setState({ isCreatePopoverOpen: false });
      return;
    }

    const handleCreateDatasource = (): void => {
      this.props.sd.cancelCreatingOrEditingDatasource();
      // Every create card's connectors at once — this button means "a new data source", not
      // "a new database".
      this.setState({ isCreatePopoverOpen: true });
    };

    if (
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName
    ) {
      this.props.setNextCallbackFunction(handleCreateDatasource);
      return;
    }

    handleCreateDatasource();
  }

  /** A column's or a parameter's type glyph, in the colour of the text beside it. */
  renderTypeGlyph = (glyph: TypeGlyph | undefined): React.ReactNode => {
    if (!glyph) return null;
    const asset: ITypeGlyphAsset = TYPE_GLYPH_ASSETS[glyph];
    return (
      <KuikaSvg type={asset.type} name={asset.name} width={16} height={16} className="kuika__data-diagram__leaf-icon" />
    );
  };

  getTypeIconGenericly = (datasourceType: EDatasourceType, type: string | undefined): TypeGlyph | undefined => {
    switch (datasourceType) {
      case EDatasourceType.MsSql:
      case EDatasourceType.CustomDb:
      case EDatasourceType.ManagedDb:
        return this.getTypeIcon(type);
      case EDatasourceType.Postgre:
      case EDatasourceType.ManagedDbPostgre:
        return this.getTypeIconPostgre(type);
      case EDatasourceType.Oracle:
      case EDatasourceType.ManagedDbOracle:
        return this.getTypeIconOracle(type);
      default:
        return undefined;
    }
  };

  getTypeIcon = (type: string | undefined): TypeGlyph => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return "string";
      case "boolean":
        return "boolean";
      case "bytea":
        return "double";
      case "byte":
        return "double";
      case "date":
        return "date";
      case "datetime":
      case "datetime2":
        return "date-time";
      case "location":
        return "location";
      case "filecontent":
        return "file-content";
      case "datetimeoffset":
        return "date-time-offset";
      case "TIMESTAMP(6) WITH TIME ZONE".toLowerCase():
      case "timestamp with time zone".toLowerCase():
        return "date-time-offset";
      case "decimal":
      case "numeric":
        return "decimal";
      case "NUMBER".toLowerCase():
        return "decimal";
      case "double":
      case "double precision":
        return "double";
      case "float":
        return "double";
      case "varbinary":
        return "double";
      case "guid":
        return "guid";
      case "int16":
        return "int16";
      case "int32":
        return "int32";
      case "int64":
        return "int64";
      case "single":
        return "double";
      case "string":
        return "string";
      case "image":
        return "image";
      case "int":
        return "int32";
      case "bigint":
        return "int64";
      case "smallint":
        return "int16";
      case "nvarchar":
        return "string";
      case "NVARCHAR2".toLowerCase():
        return "string";
      case "uniqueidentifier":
        return "guid";
      case "RAW".toLowerCase():
        return "guid";
      case "bit":
        return "boolean";
      default:
        return "string";
    }
  };

  getTypeIconPostgre = (type: string | undefined): TypeGlyph => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return "string";
      case "boolean":
      case "bit":
        return "boolean";
      case "bytea":
      case "blob":
        return "double";
      case "byte":
        return "double";
      case "date":
        return "date";
      case "datetime":
      case "timestamp":
        return "date-time";
      case "location":
        return "location";
      case "filecontent":
        return "file-content";
      case "datetimeoffset":
      case "timestamp with time zone":
        return "date-time-offset";
      case "decimal":
      case "number":
      case "numeric":
      case "integer":
        return "decimal";
      case "double":
      case "float":
      case "binary_float":
      case "binary_double":
      case "double precision":
        return "double";
      case "guid":
      case "raw":
      case "uuid":
        return "guid";
      case "int16":
      case "smallint":
        return "int16";
      case "int32":
      case "int":
        return "int32";
      case "int64":
      case "bigint":
        return "int64";
      case "single":
        return "double";
      case "string":
      case "nvarchar":
      case "varchar2":
      case "varchar":
      case "nchar":
      case "clob":
      case "nclob":
      case "text":
      case "character varying":
        return "string";
      case "image":
        return "image";
      case "char":
      case "uniqueidentifier":
        return "guid";
      default:
        return "string";
    }
  };

  getTypeIconOracle = (type: string | undefined): TypeGlyph => {
    switch (type?.toLowerCase()) {
      case "unknown":
        return "string";
      case "boolean":
      case "bit":
        return "boolean";
      case "bytea":
      case "blob":
        return "double";
      case "byte":
        return "double";
      case "date":
        return "date";
      case "datetime":
      case "timestamp":
        return "date-time";
      case "location":
        return "location";
      case "filecontent":
        return "file-content";
      case "datetimeoffset":
        return "date-time-offset";
      case "decimal":
      case "number":
        return "decimal";
      case "double":
      case "float":
      case "binary_float":
      case "binary_double":
        return "double";
      case "guid":
      case "raw":
        return "guid";
      case "int16":
      case "smallint":
        return "int16";
      case "int32":
      case "int":
        return "int32";
      case "int64":
      case "bigint":
        return "int64";
      case "single":
        return "double";
      case "string":
      case "nvarchar":
      case "varchar2":
      case "nchar":
      case "clob":
      case "nclob":
        return "string";
      case "image":
        return "image";
      case "char":
      case "uniqueidentifier":
        return "guid";
      default:
        return "string";
    }
  };

  getRestApiEndpointByIndex = (ds: IDatasourceDto, index: number) => {
    if (ds.datasourceType !== EDatasourceType.RestApiWOSwagger || index === -1) return "";
    const paths = ds.restApiConnectionRequest.paths;
    if (!paths?.length || !paths[index]) return `${ds.name}'s Endpoint`;
    return paths[index].operations[0].operationName;
  };

  render() {
    const managedDB = this.getManagedDB();
    const hasAccessManageDatasources = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageDatasource,
      this.props.sd.project.projectAccess
    );
    if (this.props.sd?.datasources) {
      return (
        <>
          <DeleteModal
            itemName={this.state.folderToDelete?.folder.name ?? ""}
            triggerModal={this.state.isDeleteFolderModalVisible}
            deleteItem={() => this.deleteFolder()}
            deleteType={"Folder"}
            closeModal={() => this.setState({ isDeleteFolderModalVisible: false })}
          />
          <DeleteModal
            itemName={this.state.tableToDelete?.title ?? ""}
            triggerModal={this.state.isDeleteTableModalVisible}
            deleteItem={() => this.state.tableToDelete && this.deleteTable(this.state.tableToDelete)}
            deleteType={"Table"}
            closeModal={() => this.setState({ isDeleteTableModalVisible: false })}
          />
          <ActionUsagesModal
            showModal={this.state.isActionUsagesModalVisible}
            closeModal={() => this.setState({ isActionUsagesModalVisible: false })}
            actionId={this.state.actionToInspectUsages?.id}
            actionName={this.state.actionToInspectUsages?.name}
          />
          <DeleteModal
            itemName={this.state.actionToDelete.name}
            triggerModal={this.state.isDeleteSqlActionModalVisible}
            deleteItem={() => this.deleteSqlAction()}
            deleteType={"Action"}
            closeModal={() => this.setState({ isDeleteSqlActionModalVisible: false })}
            extraText={(() => {
              if (!this.state.actionToDelete?.id) return undefined;
              const usages = findAgentUsagesOfAction(
                this.props.sd?.actionDefinitions ?? [],
                this.state.actionToDelete.id
              );
              if (!usages.length) return undefined;
              return (
                <span className="kuika__data-diagram__delete-warning">
                  This action is used as a tool by {usages.length === 1 ? "agent" : "agents"}: {usages.join(", ")}.
                  Deleting it will leave a broken reference in {usages.length === 1 ? "that agent" : "those agents"}.
                </span>
              );
            })()}
          />
          <DeleteModal
            itemName={this.state.datasourceToDelete.name}
            triggerModal={this.state.isDeleteDatasourceModalVisible}
            deleteItem={() => this.deleteDatasource()}
            deleteType={"Datasource"}
            closeModal={() => this.setState({ isDeleteDatasourceModalVisible: false })}
          />

          <DeleteModal
            itemName={this.getRestApiEndpointByIndex(
              this.state.datasourceToDelete,
              this.state.restApiEndpointIndexToDelete
            )}
            triggerModal={this.state.isDeleteRestApiEndpointModalVisible}
            deleteItem={() => this.deleteRestApiEndpoint()}
            deleteType={"Rest API Endpoint"}
            extraText={(() => {
              if (!this.state.actionToDelete?.id) return undefined;
              const usages = findAgentUsagesOfAction(
                this.props.sd?.actionDefinitions ?? [],
                this.state.actionToDelete.id
              );
              if (!usages.length) return undefined;
              return (
                <span className="kuika__data-diagram__delete-warning">
                  This action is used as a tool by {usages.length === 1 ? "agent" : "agents"}: {usages.join(", ")}.
                  Deleting it will leave a broken reference.
                </span>
              );
            })()}
            closeModal={() =>
              this.setState({
                isDeleteRestApiEndpointModalVisible: false,
                datasourceToDelete: {} as IDatasourceDto,
                restApiEndpointIndexToDelete: -1
              })
            }
          />

          {this.state.isNewFolderModalActive && (
            <FolderCreateComponent
              showModal={this.state.isNewFolderModalActive}
              closeModal={this.closeNewFolderModal}
              dataSourceType={this.state.editingDatasourceId}
              folderParentFolderId={this.state.folderParentFolderId}
            ></FolderCreateComponent>
          )}
          {this.state.isEditFolderModalActive && (
            <FolderCreateComponent
              showEditModal={this.state.isEditFolderModalActive}
              editingFolderId={this.state.editingFolderId}
              editingFolderName={this.state.editingFolderName}
              closeEditModal={this.closeEditFolderModal}
              folderParentFolderId={this.state.folderParentFolderId}
            ></FolderCreateComponent>
          )}
          {this.renderTableFolderRenameDialog()}
          {/* {this.props.sd.queryBuilderAction && <QueryBuilderModal showModal={this.props.sd.queryBuilderIsOpen} />} */}
          {this.props.sd.getVisibility(ViewPart.DataDiagramSchemePanel) === Visibility.Visible && (
            <>
              <div className="kuika__data-diagram">
                <div className="kuika_topPanel">
                  <SearchablePanelHeader
                    searchText={this.state.searchText}
                    onSearchTextChange={this.onSearchChange}
                    onSearchClearCallback={this.deleteSearchText}
                    viewPart={ViewPart.DataDiagramSchemePanel}
                    panelName="Sources"
                    isSearchActive={this.state.isSearchActive}
                    setSearchActive={(value: boolean) => this.setState({ isSearchActive: value })}
                    extraRightUnit={
                      hasAccessManageDatasources ? (
                        <ConnectorPickerPopover
                          category="all"
                          label="Add a data source"
                          sd={this.props.sd}
                          open={this.state.isCreatePopoverOpen}
                          onOpenChange={(open: boolean): void => this.setState({ isCreatePopoverOpen: open })}
                          collisionPadding={12}
                        >
                          <KuikaButton
                            data-testid="createDatasourcePlusButton"
                            data-onboarding-id="left-panel_datasource_plus"
                            isIcon
                            type="tertiary"
                            color="neutral"
                            active={this.state.isCreatePopoverOpen}
                            onClick={(e) => this.handleCreateOnClick(e)}
                          >
                            <KuikaSvg width={16} height={16} name="plus" />
                          </KuikaButton>
                        </ConnectorPickerPopover>
                      ) : undefined
                    }
                  />
                  <div className="kuika__data-diagram__tree">
                    {/* Only before the tree has anything to show: a reload keeps the rows on screen
                        (a rename says so in its own disabled field). */}
                    {resolveLoadingPresentation(
                      this.state.isLoading || this.state.isRenameLoading,
                      this.props.sd.datasources.length > 0
                    ) === "skeleton" && (
                      <KuikaSkeletonRegion
                        label="Loading sources"
                        className="kuika__data-diagram__skeleton kuika__data-diagram__skeleton--root"
                      >
                        <KuikaSkeletonList rows={4} density="comfortable" withIcon />
                      </KuikaSkeletonRegion>
                    )}
                    {/* The rows' own wrapper, so the selected row's fill follows folders opening above it. */}
                    <div className="kuika__data-diagram__rows">
                      <KuikaSelectionIndicator
                        activeSelector={SELECTED_SOURCE_HEADER_SELECTOR}
                        hidden={this.state.isRowDragging}
                      />
                      <KuikaCollapsePanel
                        defaultActiveKey={
                          managedDB && this.isDatabaseDiagramActive()
                            ? [managedDB.id]
                            : [this.props.sd?.activeEditingDatasource ? this.props.sd?.activeEditingDatasource.id : ""]
                        }
                        activeKey={
                          this.state.searchText.length > 0
                            ? this.state.datasourceFilteredKeys
                            : this.state.datasourceActiveKeys
                        }
                        onChange={(keys) => this.onDatasourcePanelChange(keys as string[])}
                        expandIcon={renderTreeChevron}
                        items={collapseItems([
                          ...(this.props.sd?.datasources?.length > 0
                            ? this.props.sd?.datasources
                                ?.sort((x, y) => {
                                  if (x.id === managedDB?.id) return -1;
                                  if (y.id === managedDB?.id) return 1;
                                  return 0;
                                })
                                ?.filter((datasource) => {
                                  // KOA-14774-1: Hide AIActions, AIAgents, VisionActions from datasources menu — they are in AI modules menu
                                  if (
                                    datasource.datasourceType === EDatasourceType.AIActions ||
                                    datasource.datasourceType === EDatasourceType.AIAgent ||
                                    datasource.datasourceType === EDatasourceType.VisionAction
                                  )
                                    return false;

                                  return this.checkSearch(datasource);
                                })
                                ?.map((datasource) => {
                                  // AI Agents and Vision Actions never reach here — the filter above drops both
                                  // from this list; they live in the AI modules menu (KOA-14774-1).
                                  return this.state.isRenaming && this.state.renamingId === datasource.id
                                    ? // The row being renamed keeps its place in the list, as a row with nothing to open.
                                      {
                                        key: datasource.id,
                                        label: this.renderRenameField({
                                          item: datasource,
                                          datasource,
                                          defaultValue: datasource.name,
                                          kind: "Datasource",
                                          inputTestId: "datasourceRenameInput",
                                          confirmTestId: "datasourceRenameConfirmButton"
                                        }),
                                        // Only the (hidden) arrow may toggle, so the header is not a button
                                        // around the field. "disabled" would put aria-disabled on the header,
                                        // and that disables the rename input for assistive tech and Playwright.
                                        collapsible: "icon" as const,
                                        showArrow: false,
                                        children: null
                                      }
                                    : {
                                        label: this.renderDatabaseHeader(datasource.id, datasource.name),
                                        key: datasource.id,
                                        // No menu on the managed database — `getDatasourceMenuItems`.
                                        extra: this.datasourceDropdown(datasource),
                                        className:
                                          (managedDB?.id === datasource.id && this.isDatabaseDiagramActive()) ||
                                          this.props.sd?.activeEditingDatasource?.id === datasource.id
                                            ? "kuika-folderList__item-selected kuika__data-diagram__database"
                                            : "kuika__data-diagram__database",
                                        children: (
                                          <KuikaCollapsePanel
                                            activeKey={this.state.actionActiveKeys}
                                            onChange={(keys) => this.onActionPanelChange(keys as string[])}
                                            expandIcon={renderTreeChevron}
                                            items={collapseItems([
                                              this.state.searchText.length > 0 &&
                                              this.props.sd.actionDefinitions
                                                ?.filter((x) => x.kDatasource_Id === datasource.id)
                                                ?.filter((action) =>
                                                  this.filterActionBasedOnIncludesDatasourceName(action, datasource)
                                                ).length === 0
                                                ? undefined
                                                : this.renderActionPanel(datasource),
                                              datasource.datasourceType != EDatasourceType.RestApi &&
                                                datasource.datasourceType != EDatasourceType.RestApiWOSwagger &&
                                                datasource.datasourceType != EDatasourceType.CSharpFunctions &&
                                                datasource.datasourceType !=
                                                  EDatasourceType.CustomAuthenticationProvider &&
                                                datasource.datasourceType !=
                                                  EDatasourceType.CustomAuthorizationProvider &&
                                                datasource.datasourceType !=
                                                  EDatasourceType.CustomVerificationCodeSenderProvider &&
                                                datasource.datasourceType !=
                                                  EDatasourceType.CustomConnectionStringProvider &&
                                                datasource.datasourceType !=
                                                  EDatasourceType.CustomExternalTokenValidatorProvider &&
                                                datasource.datasourceType != EDatasourceType.CustomMailProvider &&
                                                datasource.datasourceType != EDatasourceType.BleService &&
                                                datasource.datasourceType != EDatasourceType.Mqtt &&
                                                datasource.datasourceType != EDatasourceType.Blockly &&
                                                !isInlineCodeDatasourceType(datasource.datasourceType) &&
                                                datasource.datasourceType != EDatasourceType.OData &&
                                                datasource.datasourceType != EDatasourceType.SAP &&
                                                (this.state.searchText.length > 0
                                                  ? this.props.sd?.schemas
                                                      .get(datasource.id)
                                                      ?.tables?.filter((table) =>
                                                        this.filterTableBasedOnIncludesDatasourceName(table, datasource)
                                                      ).length
                                                    ? this.renderTablePanel(datasource)
                                                    : undefined
                                                  : this.renderTablePanel(datasource)),
                                              this.state.sessionVariables &&
                                                datasource.connectionString === "" &&
                                                datasource.datasourceType == EDatasourceType.ManagedDb &&
                                                this.renderSessionVariablesPanel()
                                            ])}
                                          />
                                        )
                                      };
                                })
                            : []),
                          this.renderDocumentParametersPanel()
                        ])}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      );
    }
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName)),
    setNextCallbackFunction: (cb: () => void) => dispatch(setNextCallbackFunction(cb))
  };
};

const dataDiagramSchemeWindow = connect(
  null,
  mapDispatchToProps
)(withSD<IDataDiagramSchemeWindowProps>(DataDiagramSchemeWindow));
export { dataDiagramSchemeWindow as DataDiagramSchemeWindow };
